import { useEffect, useRef, useState, type ChangeEvent } from "react";
import { Link } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import { trpc } from "../lib/trpc";
import { Icon } from "../components/ui/Icon";
import { Drawer } from "../components/Drawer";
import { ConfirmDialog } from "../components/ui/ConfirmDialog";
import { HardDeleteConfirm } from "../components/ui/HardDeleteConfirm";
import { ToggleSwitch } from "../components/ui/ToggleSwitch";
import { pushSupported, notificationPermission, currentSubscriptionEndpoint, subscribeToPush, unsubscribeFromPush } from "../lib/push";
import { TesisPickerField, useTesisPicker, resolveTesisId } from "../components/ui/TesisPickerField";
import { PAGES } from "../lib/pages";
import { useIsMobile } from "../lib/useIsMobile";
import { MCard } from "../components/mobile/primitives";
import { parseTrNumber } from "../lib/trNumber";
import "./Items.css";
import "./Settings.css";

type Tab = "allergen" | "itemType" | "customField" | "company" | "bank" | "workSchedule" | "payrollPolicy" | "notifications" | "groups" | "maintenance";
type ItemKind = "stock" | "semi" | "finished";

const KIND_LABEL: Record<ItemKind, string> = { stock: "Stok", semi: "Yarı Mamul", finished: "Mamul" };
const TYPE_LABEL: Record<string, string> = { number: "Sayı", text: "Metin", select: "Seçim", boolean: "Evet/Hayır" };

/** Masaüstü `badge-pill` renk sınıfını mobil `badge` sınıfına çevir (b-warn → b-warning). */
const mBadge = (cls: string) => (cls === "b-warn" ? "b-warning" : cls || "b-neutral");

const SETTINGS_TABS: Array<[Tab, string]> = [
  ["allergen", "Alerjenler"],
  ["itemType", "Kart Türleri"],
  ["customField", "Özel Alanlar"],
  ["company", "Firma Bilgileri"],
  ["bank", "Bankalar"],
  ["workSchedule", "Çalışma Düzeni"],
  ["notifications", "Bildirimler"],
  ["groups", "Gruplar & Yetkiler"],
];

export function SettingsPage() {
  const [tab, setTab] = useState<Tab>("allergen");
  const isMobile = useIsMobile();
  const meQ = trpc.auth.me.useQuery();
  const isSuperAdmin = !!meQ.data?.user?.isSuperAdmin;

  const activeSection = tab === "allergen" ? <AllergensSection /> : tab === "itemType" ? <ItemTypesSection /> : tab === "customField" ? <CustomFieldsSection /> : tab === "company" ? <CompanyProfileSection /> : tab === "bank" ? <BanksSection /> : tab === "workSchedule" ? <WorkScheduleSection /> : tab === "payrollPolicy" ? (isSuperAdmin ? <PayrollPolicySection /> : null)  : tab === "notifications" ? <NotificationsSection isSuperAdmin={isSuperAdmin} /> : tab === "groups" ? <GroupsSection /> : tab === "maintenance" ? (isSuperAdmin ? <MaintenanceSection /> : null) : null;

  if (isMobile) {
    return (
      <>
        <div className="m-stickytop">
          <div className="m-chips">
            {SETTINGS_TABS.map(([k, lb]) => (
              <button key={k} type="button" className={`m-chip${tab === k ? " act" : ""}`} onClick={() => setTab(k)}>{lb}</button>
            ))}
            {isSuperAdmin ? <button type="button" className={`m-chip${tab === "payrollPolicy" ? " act" : ""}`} onClick={() => setTab("payrollPolicy")}>Bordro Politikası</button> : null}
            {isSuperAdmin ? <button type="button" className={`m-chip${tab === "maintenance" ? " act" : ""}`} onClick={() => setTab("maintenance")}>Sistem Bakımı</button> : null}
          </div>
        </div>
        {activeSection}
      </>
    );
  }

  return (
    <div className="items-page">
      <div className="items-crumb">
        <Link to="/dashboard">Panel</Link>
        <span className="sep">/</span>
        <span className="cur">Ayarlar</span>
      </div>

      <div className="items-head">
        <div>
          <h1 className="title">Ayarlar</h1>
          <p className="sub">Alerjen listesi · kart türleri · özel alanlar — kendi yapılandırman</p>
        </div>
      </div>

      <div className="dt2-wrap">
        <div className="dt2-tabs">
          <button type="button" className={`dt2-tab ${tab === "allergen" ? "active" : ""}`} onClick={() => setTab("allergen")}>Alerjenler</button>
          <button type="button" className={`dt2-tab ${tab === "itemType" ? "active" : ""}`} onClick={() => setTab("itemType")}>Kart Türleri</button>
          <button type="button" className={`dt2-tab ${tab === "customField" ? "active" : ""}`} onClick={() => setTab("customField")}>Özel Alanlar</button>
          <button type="button" className={`dt2-tab ${tab === "company" ? "active" : ""}`} onClick={() => setTab("company")}>Firma Bilgileri</button>
          <button type="button" className={`dt2-tab ${tab === "bank" ? "active" : ""}`} onClick={() => setTab("bank")}>Bankalar</button>
          <button type="button" className={`dt2-tab ${tab === "workSchedule" ? "active" : ""}`} onClick={() => setTab("workSchedule")}>Çalışma Düzeni</button>
          {isSuperAdmin ? <button type="button" className={`dt2-tab ${tab === "payrollPolicy" ? "active" : ""}`} onClick={() => setTab("payrollPolicy")}>Bordro Politikası</button> : null}
          <button type="button" className={`dt2-tab ${tab === "notifications" ? "active" : ""}`} onClick={() => setTab("notifications")}>Bildirimler</button>
          <button type="button" className={`dt2-tab ${tab === "groups" ? "active" : ""}`} onClick={() => setTab("groups")}>Gruplar & Yetkiler</button>
          {isSuperAdmin ? <button type="button" className={`dt2-tab ${tab === "maintenance" ? "active" : ""}`} onClick={() => setTab("maintenance")} style={{ color: tab === "maintenance" ? undefined : "var(--danger)" }}>Sistem Bakımı</button> : null}
        </div>
        <div style={{ padding: 18 }}>
          {activeSection}
        </div>
      </div>
    </div>
  );
}

/* ── Ortak: aktif/pasif satır toggle butonu ── */
function ToggleBtn({ active, busy, onClick }: { active: boolean; busy?: boolean; onClick: () => void }) {
  return (
    <button
      type="button"
      className={`iact ${active ? "danger" : "success"}`}
      aria-label={active ? "Pasifleştir" : "Aktifleştir"}
      title={active ? "Pasifleştir" : "Aktifleştir"}
      onClick={onClick}
      disabled={busy}
    >
      <Icon name={active ? "archive" : "check"} size={15} />
    </button>
  );
}

/* ─── Kullanıcı Grupları & Yetkiler (RBAC yönetimi) ─────────────────── */
const PERM_RESOURCE_LABEL: Record<string, string> = {
  item: "Kartlar / Ürünler",
  location: "Lokasyonlar",
  recipe: "Reçeteler",
  movement: "Stok Hareketleri",
  lot: "Lot / Kalite",
  work_order: "Üretim",
  order: "Siparişler",
  user: "Kullanıcı Yönetimi",
  role: "Grup Yönetimi",
  audit_log: "Denetim Kaydı",
};

interface GroupRow {
  id: string; code: string; name: string; description: string | null;
  pages: string[]; isSystem: boolean; userCount: number;
  tesisId?: string | null; tesisName?: string | null;
  permissions: Array<{ permission: { code: string; name: string } }>;
}

/**
 * Bildirim tercihleri — kullanıcı hangi olayların KENDİSİNE bildirim üreteceğini seçer (yalnız yetkisinin
 * kapsadığı olaylar listelenir). Süper-admin ek olarak GLOBAL aç/kapa yapabilir (bir olay sistemde hiç üretilsin mi).
 */
/** Bildirim kategorisi → başlık ikonu (yalnız Icon.tsx'te var olan adlar). */
const NOTIF_CAT_ICON: Record<string, string> = {
  order: "package",
  production: "layers",
  quality: "checkCircle",
  shipment: "truck",
  stock: "archive",
  maintenance: "wrench",
  purchasing: "cart",
  hr: "user",
  project: "list2",
};

/** "Bu cihazda push bildirimleri" — izin iste + PushManager aboneliği + backend'e kaydet. */
function PushDeviceCard() {
  const cfgQ = trpc.notification.pushConfig.useQuery();
  const subMut = trpc.notification.subscribePush.useMutation();
  const unsubMut = trpc.notification.unsubscribePush.useMutation();

  const supported = pushSupported();
  const [subscribed, setSubscribed] = useState<boolean | null>(null); // null = denetleniyor
  const [perm, setPerm] = useState<NotificationPermission | "unsupported">(() => notificationPermission());
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState<string | null>(null);

  useEffect(() => {
    let alive = true;
    (async () => {
      if (!supported) { if (alive) setSubscribed(false); return; }
      const ep = await currentSubscriptionEndpoint();
      if (alive) setSubscribed(!!ep);
    })();
    return () => { alive = false; };
  }, [supported]);

  const serverEnabled = cfgQ.data?.enabled ?? false;
  const publicKey = cfgQ.data?.publicKey ?? null;

  async function enable() {
    setBusy(true); setMsg(null);
    try {
      if (!publicKey) { setMsg("Sunucuda push yapılandırılmamış."); return; }
      const data = await subscribeToPush(publicKey);
      if (!data) {
        const p = notificationPermission();
        setPerm(p);
        setMsg(p === "denied"
          ? "Bildirim izni reddedilmiş — tarayıcı/site ayarlarından izin verin."
          : "Bu cihazda abonelik kurulamadı. Uygulamayı ana ekrana ekleyin (iOS) ya da güvenli (HTTPS) adreste açın.");
        return;
      }
      await subMut.mutateAsync({ endpoint: data.endpoint, keys: data.keys, userAgent: navigator.userAgent.slice(0, 400) });
      setSubscribed(true); setPerm("granted");
    } catch (e) {
      setMsg((e as Error)?.message ?? "Abonelik hatası");
    } finally { setBusy(false); }
  }

  async function disable() {
    setBusy(true); setMsg(null);
    try {
      const ep = await unsubscribeFromPush();
      if (ep) await unsubMut.mutateAsync({ endpoint: ep });
      setSubscribed(false);
    } catch (e) {
      setMsg((e as Error)?.message ?? "İptal hatası");
    } finally { setBusy(false); }
  }

  const status = !supported
    ? "Bu tarayıcı push bildirimini desteklemiyor."
    : !serverEnabled
      ? "Sunucuda push bildirimi kapalı."
      : subscribed === null
        ? "Denetleniyor…"
        : subscribed
          ? "Açık — bu cihaz/tarayıcı bildirimleri alıyor."
          : perm === "denied"
            ? "İzin reddedilmiş — tarayıcı/site ayarlarından açabilirsiniz."
            : "Kapalı — açınca uygulama kapalıyken bile bildirim gelir.";

  const toggleDisabled = busy || !supported || !serverEnabled || subscribed === null || (perm === "denied" && !subscribed);

  return (
    <div className="set-ntf-device">
      <span className="ic"><Icon name="bell" size={18} /></span>
      <div className="body">
        <div className="t">Bu cihazda push bildirimleri</div>
        <div className="s">{status}{msg ? <span className="err"> · {msg}</span> : null}</div>
      </div>
      <ToggleSwitch
        checked={!!subscribed}
        disabled={toggleDisabled}
        onChange={(v) => (v ? enable() : disable())}
        ariaLabel="Bu cihazda push bildirimleri"
      />
    </div>
  );
}

function NotificationsSection({ isSuperAdmin }: { isSuperAdmin: boolean }) {
  const catQ = trpc.notification.catalog.useQuery();
  const setPref = trpc.notification.setPref.useMutation({ onSuccess: () => catQ.refetch() });
  const setCfg = trpc.notification.setEventConfig.useMutation({ onSuccess: () => catQ.refetch() });
  const data = catQ.data;

  // kategoriye göre grupla (katalog sırası korunur)
  const groups: Array<{ category: string; label: string; events: NonNullable<typeof data>["events"] }> = [];
  const idx = new Map<string, number>();
  for (const e of data?.events ?? []) {
    if (!idx.has(e.category)) { idx.set(e.category, groups.length); groups.push({ category: e.category, label: e.categoryLabel, events: [] }); }
    groups[idx.get(e.category)!]!.events.push(e);
  }

  const groupHead = (category: string, label: string) => (
    <div className="set-ntf-glabel"><span className="ic"><Icon name={NOTIF_CAT_ICON[category] ?? "bell"} size={13} /></span>{label}</div>
  );

  return (
    <section className="d-card">
      <div className="d-card-head">
        <div className="left"><span className="t">Bildirimler</span></div>
      </div>
      <div className="d-card-body">
        <PushDeviceCard />
        {catQ.isLoading ? (
          <p className="td-empty">Yükleniyor…</p>
        ) : !data || data.events.length === 0 ? (
          <p className="td-empty">Görüntülenecek bildirim olayı yok — yetkin dahilinde olay bulunmuyor.</p>
        ) : isSuperAdmin ? (
          /* SÜPER-ADMIN matris: her satırda «Bana» (kişisel tercih) + «Sistemde» (global üretim) */
          <>
            <p className="set-ntf-intro">
              <b>Bana</b> sütunu bu olayın sana bildirim gönderip göndermeyeceğini; <b>Sistemde</b> sütunu ise olayın
              hiçbir kullanıcıya üretilip üretilmeyeceğini belirler. Sistemde kapalı bir olay kimseye bildirim üretmez.
            </p>
            <div className="set-ntf-groups set-ntf-matrix">
              <div className="set-ntf-colhead">
                <span className="lbl" />
                <span className="col">Bana</span>
                <span className="col">Sistemde</span>
              </div>
              {groups.map((g) => (
                <div className="set-ntf-group" key={g.category}>
                  {groupHead(g.category, g.label)}
                  {g.events.map((e) => (
                    <div className={`set-ntf-mrow ${e.globalEnabled ? "" : "off"}`} key={e.key}>
                      <span className="lbl" title={e.label}>{e.label}</span>
                      <span className="set-ntf-mtoggle">
                        <ToggleSwitch checked={e.userEnabled} disabled={!e.globalEnabled} onChange={(v) => setPref.mutate({ eventKey: e.key, enabled: v })} ariaLabel={`${e.label} — bana bildir`} />
                      </span>
                      <span className="set-ntf-mtoggle">
                        <ToggleSwitch checked={e.globalEnabled} onChange={(v) => setCfg.mutate({ eventKey: e.key, enabled: v })} ariaLabel={`${e.label} — sistemde üret`} />
                      </span>
                    </div>
                  ))}
                </div>
              ))}
            </div>
          </>
        ) : (
          /* NORMAL kullanıcı: 2 sütunlu kompakt grid */
          <>
            <p className="set-ntf-intro">Hangi durumların sana bildirim üreteceğini seç. Yalnız yetkinin kapsadığı olaylar listelenir.</p>
            <div className="set-ntf-groups">
              {groups.map((g) => (
                <div className="set-ntf-group" key={g.category}>
                  {groupHead(g.category, g.label)}
                  <div className="set-ntf-grid">
                    {g.events.map((e) => (
                      <div className="set-ntf-row" key={e.key}>
                        <span className="lbl" title={e.label}>{e.label}</span>
                        <ToggleSwitch checked={e.userEnabled} onChange={(v) => setPref.mutate({ eventKey: e.key, enabled: v })} ariaLabel={`${e.label} bildirimi`} />
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </>
        )}
      </div>
    </section>
  );
}

function GroupsSection() {
  const qc = useQueryClient();
  const isMobile = useIsMobile();
  const listQ = trpc.role.list.useQuery();
  const catalogQ = trpc.role.permissionCatalog.useQuery();
  const deleteMut = trpc.role.delete.useMutation();

  const [editorOpen, setEditorOpen] = useState(false);
  const [editing, setEditing] = useState<GroupRow | null>(null);
  const [delTarget, setDelTarget] = useState<GroupRow | null>(null);

  const rows = (listQ.data ?? []) as GroupRow[];

  function runDelete() {
    if (!delTarget) return;
    deleteMut.mutate({ id: delTarget.id }, { onSuccess: () => { setDelTarget(null); deleteMut.reset(); qc.invalidateQueries(); } });
  }

  const drawers = (
    <>
      <GroupEditorDrawer
        open={editorOpen}
        group={editing}
        catalog={catalogQ.data ?? []}
        onClose={() => setEditorOpen(false)}
      />
      <ConfirmDialog
        open={!!delTarget}
        title="Grubu sil"
        message={<>"<b>{delTarget?.name}</b>" grubu kalıcı olarak silinecek. Devam edilsin mi?</>}
        confirmLabel="Sil"
        tone="danger"
        busy={deleteMut.isPending}
        onConfirm={runDelete}
        onCancel={() => setDelTarget(null)}
      />
      {delTarget && deleteMut.error ? <div className="form-banner-err" style={{ marginTop: 8 }}>{deleteMut.error.message}</div> : null}
    </>
  );

  if (isMobile) {
    return (
      <>
        <div className="m-stickytop">
          <button type="button" className="btn-md primary" style={{ justifyContent: "center" }} onClick={() => { setEditing(null); setEditorOpen(true); }}><Icon name="plus" size={14} />Yeni Grup</button>
        </div>
        {deleteMut.error && !delTarget ? <div className="form-banner-err" role="alert">{deleteMut.error.message}</div> : null}
        {listQ.isLoading ? <div className="m-empty">Yükleniyor…</div> : rows.length === 0 ? <div className="m-empty">Henüz grup tanımlanmadı.</div> : (
          rows.map((r) => (
            <MCard pad key={r.id}>
              <div style={{ display: "flex", alignItems: "flex-start", gap: 8 }}>
                <span style={{ flex: 1, minWidth: 0 }}>
                  <span style={{ display: "block", fontSize: 13.5, fontWeight: 700, color: "var(--neutral-900)" }}>{r.name}</span>
                  {r.description ? <span style={{ display: "block", fontSize: 11.5, color: "var(--neutral-500)" }}>{r.description}</span> : null}
                  <span className="mono" style={{ display: "block", fontSize: 11, color: "var(--neutral-400)" }}>{r.code}</span>
                </span>
                {r.isSystem ? <span className="badge b-info no-dot">Sistem</span> : <span className="badge b-neutral no-dot">Özel</span>}
              </div>
              <div style={{ display: "flex", gap: 12, marginTop: 8 }}>
                <div style={{ flex: 1 }}><div className="m-ovl">Tesis</div><div style={{ fontSize: 12, fontWeight: 600, marginTop: 2 }}>{r.tesisName ?? "Global"}</div></div>
                <div style={{ flex: 1 }}><div className="m-ovl">Kullanıcı</div><div className="mono" style={{ fontSize: 12, fontWeight: 600, marginTop: 2 }}>{r.userCount}</div></div>
                <div style={{ flex: 1 }}><div className="m-ovl">Sayfa</div><div className="mono" style={{ fontSize: 12, fontWeight: 600, marginTop: 2 }}>{r.isSystem ? PAGES.length : r.pages.length}/{PAGES.length}</div></div>
                <div style={{ flex: 1 }}><div className="m-ovl">Yetki</div><div className="mono" style={{ fontSize: 12, fontWeight: 600, marginTop: 2 }}>{r.permissions.length}</div></div>
              </div>
              {r.isSystem ? (
                <div style={{ fontSize: 11, color: "var(--neutral-400)", marginTop: 8, textAlign: "right" }}>Sistem grubu düzenlenemez — tüm sayfa ve yetkilere sahiptir.</div>
              ) : (
                <div style={{ display: "flex", gap: 6, marginTop: 10, justifyContent: "flex-end" }}>
                  <button type="button" className="m-iconbtn m-li-act" aria-label="Düzenle" onClick={() => { setEditing(r); setEditorOpen(true); }}><Icon name="edit" size={16} /></button>
                  <button type="button" className="m-iconbtn m-li-act danger" aria-label="Sil" onClick={() => { deleteMut.reset(); setDelTarget(r); }}><Icon name="trash" size={16} /></button>
                </div>
              )}
            </MCard>
          ))
        )}
        {drawers}
      </>
    );
  }

  return (
    <div>
      <div style={{ display: "flex", alignItems: "center", marginBottom: 14, gap: 12 }}>
        <p className="sub" style={{ margin: 0 }}>Kullanıcı grupları: hangi sayfalara erişir, hangi işlemleri yapabilir. Kullanıcılar sayfasından gruba atanır.</p>
        <span style={{ flex: 1 }} />
        <button type="button" className="btn-md primary" onClick={() => { setEditing(null); setEditorOpen(true); }}><Icon name="plus" size={14} />Yeni Grup</button>
      </div>
      {deleteMut.error && !delTarget ? <div className="form-banner-err">{deleteMut.error.message}</div> : null}

      {listQ.isLoading ? (
        <div className="empty-state"><p className="ttl">Yükleniyor…</p></div>
      ) : (
        <table className="dt2">
          <thead>
            <tr>
              <th>Grup</th>
              <th style={{ width: 110 }}>Kod</th>
              <th style={{ width: 140 }}>Tesis</th>
              <th style={{ width: 110 }}>Kullanıcı</th>
              <th style={{ width: 110 }}>Sayfa</th>
              <th style={{ width: 110 }}>Yetki</th>
              <th style={{ width: 100 }}>Tür</th>
              <th style={{ width: 96 }}>Eylemler</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r.id}>
                <td>{r.name}{r.description ? <span className="sec">{r.description}</span> : null}</td>
                <td><span className="cell-id">{r.code}</span></td>
                <td style={{ fontSize: 12.5 }}>{r.tesisName ?? <span style={{ color: "var(--neutral-400)" }}>Global</span>}</td>
                <td><span className="mono" style={{ fontSize: 12.5 }}>{r.userCount}</span></td>
                <td><span className="mono" style={{ fontSize: 12.5 }}>{r.isSystem ? PAGES.length : r.pages.length}</span><span className="unit">/{PAGES.length}</span></td>
                <td><span className="mono" style={{ fontSize: 12.5 }}>{r.permissions.length}</span></td>
                <td>{r.isSystem ? <span className="badge-pill b-info">Sistem</span> : <span className="badge-pill">Özel</span>}</td>
                <td>
                  <div className="row-acts">
                    {r.isSystem ? (
                      <span style={{ fontSize: 11, color: "var(--neutral-400)" }} title="Sistem grubu düzenlenemez — tüm sayfa ve yetkilere sahiptir">—</span>
                    ) : (
                      <>
                        <button type="button" className="iact" aria-label="Düzenle" title="Düzenle" onClick={() => { setEditing(r); setEditorOpen(true); }}><Icon name="edit" size={15} /></button>
                        <button type="button" className="iact danger" aria-label="Sil" title="Sil" onClick={() => { deleteMut.reset(); setDelTarget(r); }}><Icon name="trash" size={15} /></button>
                      </>
                    )}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      {drawers}
    </div>
  );
}

function GroupEditorDrawer({ open, group, catalog, onClose }: {
  open: boolean;
  group: GroupRow | null;
  catalog: Array<{ code: string; name: string }>;
  onClose: () => void;
}) {
  const qc = useQueryClient();
  const isEdit = !!group;
  const createMut = trpc.role.create.useMutation();
  const updateMut = trpc.role.update.useMutation();
  const pending = createMut.isPending || updateMut.isPending;

  const meQ = trpc.auth.me.useQuery(undefined, { staleTime: 5 * 60_000 });
  const isSuper = !!(meQ.data?.user as { isSuperAdmin?: boolean } | undefined)?.isSuperAdmin;
  const facilities = (meQ.data?.user as { facilities?: Array<{ id: string; code: string; name: string }> } | undefined)?.facilities ?? [];
  const activeFacilityId = (meQ.data?.user as { activeFacilityId?: string | null } | undefined)?.activeFacilityId ?? null;
  const showTesis = isSuper && facilities.length > 1;

  const [code, setCode] = useState("");
  const [name, setName] = useState("");
  const [desc, setDesc] = useState("");
  const [tesisId, setTesisId] = useState("");
  const [pages, setPages] = useState<Set<string>>(new Set());
  const [perms, setPerms] = useState<Set<string>>(new Set());
  const [err, setErr] = useState<string | null>(null);

  useEffect(() => {
    if (!open) return;
    setErr(null); createMut.reset(); updateMut.reset();
    if (group) {
      setCode(group.code); setName(group.name); setDesc(group.description ?? "");
      setTesisId(group.tesisId ?? "");
      setPages(new Set(group.pages));
      setPerms(new Set(group.permissions.map((p) => p.permission.code)));
    } else {
      setCode(""); setName(""); setDesc(""); setTesisId(activeFacilityId ?? facilities[0]?.id ?? ""); setPages(new Set()); setPerms(new Set());
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, group?.id]);

  // Yetkileri kaynak bazında grupla (view:item → item).
  const permGroups = (() => {
    const map = new Map<string, Array<{ code: string; name: string }>>();
    for (const p of catalog) {
      const res = p.code.split(":")[1] ?? "diğer";
      if (!map.has(res)) map.set(res, []);
      map.get(res)!.push(p);
    }
    return [...map.entries()].map(([res, items]) => ({ res, label: PERM_RESOURCE_LABEL[res] ?? res, items }));
  })();

  const togglePage = (k: string) => setPages((s) => { const n = new Set(s); if (n.has(k)) n.delete(k); else n.add(k); return n; });
  const togglePerm = (c: string) => setPerms((s) => { const n = new Set(s); if (n.has(c)) n.delete(c); else n.add(c); return n; });

  async function save() {
    setErr(null);
    if (!name.trim()) return setErr("Grup adı zorunlu.");
    if (!isEdit && !/^[a-z0-9_-]+$/.test(code.trim())) return setErr("Grup kodu: küçük harf, rakam, _ ve - (örn. depo_operator).");
    try {
      if (isEdit && group) {
        await updateMut.mutateAsync({ id: group.id, name: name.trim(), description: desc.trim() || null, pages: [...pages], permissionCodes: [...perms] });
      } else {
        await createMut.mutateAsync({ code: code.trim(), name: name.trim(), description: desc.trim() || null, tesisId: showTesis ? (tesisId || null) : undefined, pages: [...pages], permissionCodes: [...perms] });
      }
      await qc.invalidateQueries();
      onClose();
    } catch { /* err banner */ }
  }

  const mutErr = createMut.error?.message ?? updateMut.error?.message ?? null;

  return (
    <Drawer
      open={open}
      onClose={onClose}
      size="lg"
      title={isEdit ? "Grubu Düzenle" : "Yeni Grup"}
      subtitle={isEdit ? `Kod: ${group?.code}` : "Sayfa erişimi + işlem yetkileri"}
      footer={
        <>
          <button type="button" className="btn-md ghost" onClick={onClose} disabled={pending}>İptal</button>
          <button type="button" className="btn-md primary" onClick={save} disabled={pending}>
            {pending ? "Kaydediliyor…" : isEdit ? "Güncelle" : "Oluştur"}
          </button>
        </>
      }
    >
      {err || mutErr ? <div className="form-banner-err" role="alert">{err ?? mutErr}</div> : null}

      <div className="form-row">
        <div className="form-field">
          <label className="lbl" htmlFor="gr-name">Grup Adı <span className="req">*</span></label>
          <input id="gr-name" className="form-input" value={name} onChange={(e) => setName(e.target.value)} maxLength={100} placeholder="örn. Depo Operatörü" />
        </div>
        <div className="form-field">
          <label className="lbl" htmlFor="gr-code">Grup Kodu {!isEdit ? <span className="req">*</span> : null}</label>
          <input id="gr-code" className="form-input mono" value={code} onChange={(e) => setCode(e.target.value.toLowerCase())} maxLength={50} placeholder="depo_operator" disabled={isEdit} />
          {isEdit ? <span className="help">Kod oluşturulduktan sonra değiştirilemez.</span> : null}
        </div>
      </div>
      <div className="form-field">
        <label className="lbl" htmlFor="gr-desc">Açıklama</label>
        <input id="gr-desc" className="form-input" value={desc} onChange={(e) => setDesc(e.target.value)} maxLength={300} />
      </div>
      {showTesis && !isEdit ? (
        <div className="form-field">
          <label className="lbl" htmlFor="gr-tesis">Tesis</label>
          <select id="gr-tesis" className="form-select" value={tesisId} onChange={(e) => setTesisId(e.target.value)}>
            <option value="">— Global (tüm tesisler) —</option>
            {facilities.map((t) => <option key={t.id} value={t.id}>{t.name} ({t.code})</option>)}
          </select>
          <span className="help">Grubun ait olduğu tesis. Bu tesisteki kullanıcı yöneticileri yalnız kendi tesisinin gruplarını görür.</span>
        </div>
      ) : null}

      {/* Sayfa erişimi */}
      <div style={{ marginTop: 12, marginBottom: 6 }}>
        <span className="lbl" style={{ fontSize: 13, fontWeight: 600 }}>Sayfa Erişimi</span>
        <span className="help" style={{ display: "block", marginTop: 2 }}>Grup üyeleri yalnız işaretli sayfaları görür (menü + doğrudan adres).</span>
      </div>
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(200px, 1fr))", gap: "6px 14px", padding: "10px 12px", border: "1px solid var(--neutral-200)", borderRadius: "var(--radius-md)", background: "var(--neutral-50)" }}>
        {PAGES.map((p) => (
          <label key={p.key} style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 13, cursor: "pointer" }}>
            <input type="checkbox" checked={pages.has(p.key)} onChange={() => togglePage(p.key)} style={{ width: 15, height: 15, accentColor: "var(--primary)", cursor: "pointer", flexShrink: 0 }} />
            <span>{p.label}</span>
          </label>
        ))}
      </div>

      {/* İşlem yetkileri */}
      <div style={{ marginTop: 16, marginBottom: 6 }}>
        <span className="lbl" style={{ fontSize: 13, fontWeight: 600 }}>İşlem Yetkileri</span>
        <span className="help" style={{ display: "block", marginTop: 2 }}>Sunucu tarafında zorunlu tutulur — sayfayı görse bile yetkisiz işlem yapamaz.</span>
      </div>
      <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
        {permGroups.map((g) => (
          <div key={g.res} style={{ border: "1px solid var(--neutral-200)", borderRadius: "var(--radius-md)", background: "var(--neutral-50)", padding: "8px 12px" }}>
            <div style={{ fontSize: 10.5, fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.03em", color: "var(--neutral-500)", marginBottom: 6 }}>{g.label}</div>
            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(220px, 1fr))", gap: "5px 14px" }}>
              {g.items.map((p) => (
                <label key={p.code} style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 12.5, cursor: "pointer" }}>
                  <input type="checkbox" checked={perms.has(p.code)} onChange={() => togglePerm(p.code)} style={{ width: 15, height: 15, accentColor: "var(--primary)", cursor: "pointer", flexShrink: 0 }} />
                  <span>{p.name}<span className="mono" style={{ fontSize: 10, color: "var(--neutral-400)", marginLeft: 5 }}>{p.code}</span></span>
                </label>
              ))}
            </div>
          </div>
        ))}
      </div>
    </Drawer>
  );
}

/* ─── Çalışma Düzeni: çalışma günleri + vardiyalar ─────────────────── */
/* --- Tatiller & telafi gunleri (takvim istisnalari) --------------- */

const DOW_TR = ["Pazar", "Pazartesi", "Salı", "Çarşamba", "Perşembe", "Cuma", "Cumartesi"];

/**
 * `@db.Date` UTC gece yarısı döner — gün alanları UTC ile OKUNMALI.
 * Yerel alanlarla okumak, sunucunun yazma biçimiyle (UTC gece yarısı) çelişip
 * negatif ofsetli saat dilimlerinde tarihi bir gün öteler.
 */
function exDay(d: string | Date): { key: string; text: string; dow: number } {
  const dt = new Date(d);
  const p2 = (n: number) => String(n).padStart(2, "0");
  const y = dt.getUTCFullYear(), m = dt.getUTCMonth() + 1, day = dt.getUTCDate();
  return { key: `${y}-${p2(m)}-${p2(day)}`, text: `${p2(day)}.${p2(m)}.${y}`, dow: dt.getUTCDay() };
}

/** YYYY-AA-GG metninden ISO hafta günü (1=Pzt … 7=Paz) — `workDays` ile aynı eksen. */
function isoDowOf(dateStr: string): number | null {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(dateStr)) return null;
  const d = new Date(`${dateStr}T00:00:00Z`);
  if (Number.isNaN(d.getTime())) return null;
  const js = d.getUTCDay(); // 0=Paz
  return js === 0 ? 7 : js;
}

function CalendarExceptionsSection({ workDays }: { workDays: number[] }) {
  const qc = useQueryClient();
  const isMobile = useIsMobile();
  const thisYear = new Date().getFullYear();
  const [year, setYear] = useState(thisYear);
  const [date, setDate] = useState("");
  const [isWorking, setIsWorking] = useState(false);
  const [note, setNote] = useState("");
  const [delId, setDelId] = useState<string | null>(null);

  const exQ = trpc.settings.workSchedule.listExceptions.useQuery({
    from: new Date(Date.UTC(year, 0, 1)).toISOString(),
    to: new Date(Date.UTC(year, 11, 31)).toISOString(),
  });
  const setMut = trpc.settings.workSchedule.setException.useMutation({
    onSuccess: () => { qc.invalidateQueries(); setDate(""); setNote(""); },
  });
  const delMut = trpc.settings.workSchedule.removeException.useMutation({
    onSuccess: () => { qc.invalidateQueries(); setDelId(null); },
  });

  const rows = exQ.data ?? [];
  const todayKey = exDay(new Date()).key;

  // Girilmek üzere olan istisna haftalık düzenle ÇAKIŞIYOR mu? Etkisiz kayıt
  // kullanıcıyı "tatili tanımladım" diye yanıltır — kaydetmeden önce söylenmeli.
  const dow = isoDowOf(date);
  const alreadyOff = dow != null && !workDays.includes(dow);
  const dowName = dow == null ? "" : DOW_TR[dow === 7 ? 0 : dow];
  const redundant = dow == null ? null
    : !isWorking && alreadyOff ? `${dowName} zaten çalışma günü değil — bu tatil kaydının etkisi olmaz.`
    : isWorking && !alreadyOff ? `${dowName} zaten çalışma günü — bu telafi kaydının etkisi olmaz.`
    : null;

  const years = [thisYear - 1, thisYear, thisYear + 1, thisYear + 2];

  const delDialog = (
    <ConfirmDialog
      open={!!delId}
      title="Günü sil"
      message={(() => {
        // HANGİ gün olduğu yazılmalı: "bu kayıt" diyen bir onay, yanlış satırı
        // silmeyi engellemez.
        const r = rows.find((x) => x.id === delId);
        if (!r) return "Bu takvim istisnası silinecek.";
        const d = exDay(r.date);
        return `${d.text} (${DOW_TR[d.dow]}) — ${r.isWorking ? "telafi" : "tatil"}${r.note ? ` · ${r.note}` : ""} silinecek. Proje planlaması bu günü yeniden haftalık düzene göre değerlendirir.`;
      })()}
      confirmLabel="Sil"
      onCancel={() => setDelId(null)}
      onConfirm={() => { if (delId) delMut.mutate({ id: delId }); }}
    />
  );

  if (isMobile) {
    return (
      <>
        <div className="set-hol-head" style={{ marginBottom: 8 }}>
          <span className="set-sec-title" style={{ margin: 0 }}>Tatiller & Telafi Günleri</span>
          <span className="set-hol-year">
            Yıl
            <select className="form-select" value={year} onChange={(e) => setYear(Number(e.target.value))} aria-label="Yıl">
              {years.map((y) => <option key={y} value={y}>{y}</option>)}
            </select>
          </span>
        </div>
        <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
          <label>
            <span className="m-ovl" style={{ display: "block", marginBottom: 3 }}>Tarih</span>
            <input className="form-input" type="date" value={date} onChange={(e) => setDate(e.target.value)} />
          </label>
          <label>
            <span className="m-ovl" style={{ display: "block", marginBottom: 3 }}>Tür</span>
            <select className="form-select" value={isWorking ? "work" : "off"} onChange={(e) => setIsWorking(e.target.value === "work")}>
              <option value="off">Tatil (çalışılmaz)</option>
              <option value="work">Telafi (çalışılır)</option>
            </select>
          </label>
          <label>
            <span className="m-ovl" style={{ display: "block", marginBottom: 3 }}>Açıklama</span>
            <input className="form-input" value={note} onChange={(e) => setNote(e.target.value)} maxLength={200} placeholder="örn. Ramazan Bayramı" />
          </label>
          <button type="button" className="btn-md primary" style={{ justifyContent: "center" }} disabled={!date || setMut.isPending}
            onClick={() => setMut.mutate({ date, isWorking, note: note.trim() || null })}>
            <Icon name="plus" size={14} />{setMut.isPending ? "Ekleniyor…" : "Ekle"}
          </button>
        </div>
        {setMut.error ? <div className="form-banner-err">{setMut.error.message}</div> : null}
        {redundant ? (
          <div className="set-hol-redundant" role="status" aria-live="polite">
            <Icon name="alertTriangle" size={12} />{redundant}
          </div>
        ) : null}
        {exQ.isError ? (
          <div className="form-banner-err">Tatil listesi yüklenemedi: {exQ.error.message}</div>
        ) : exQ.isLoading ? (
          <div className="m-empty">Yükleniyor…</div>
        ) : rows.length === 0 ? (
          <div className="m-empty">{year} için tanımlı gün yok — resmî tatiller ve telafi mesaileri buraya girilir.</div>
        ) : (
          rows.map((r) => {
            const d = exDay(r.date);
            return (
              <MCard pad key={r.id} style={d.key < todayKey ? { opacity: 0.6 } : undefined}>
                <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                  <span style={{ flex: 1, minWidth: 0 }}>
                    <span className="mono" style={{ fontSize: 13, fontWeight: 700, color: "var(--neutral-900)" }}>{d.text}</span>
                    <span style={{ fontSize: 11.5, color: "var(--neutral-500)", marginLeft: 6 }}>{DOW_TR[d.dow]}</span>
                  </span>
                  {r.isWorking ? <span className="badge b-info no-dot"><Icon name="clock" size={11} />Telafi</span> : <span className="badge b-warning no-dot"><Icon name="calendar" size={11} />Tatil</span>}
                  <button type="button" className="m-iconbtn m-li-act danger" aria-label={`${d.text} — ${r.isWorking ? "telafi" : "tatil"} kaydını sil`} onClick={() => setDelId(r.id)}><Icon name="trash" size={16} /></button>
                </div>
                {r.note ? <div style={{ fontSize: 12, color: "var(--neutral-600)", marginTop: 6 }}>{r.note}</div> : null}
              </MCard>
            );
          })
        )}
        {delMut.error ? <div className="form-banner-err">{delMut.error.message}</div> : null}
        {delDialog}
      </>
    );
  }

  return (
    <div className="set-sec">
      <div className="set-hol-head">
        <span className="set-sec-title">Tatiller & Telafi Günleri</span>
        <span className="set-hol-year">
          Yıl
          <select className="form-select" value={year} onChange={(e) => setYear(Number(e.target.value))} aria-label="Yıl">
            {years.map((y) => <option key={y} value={y}>{y}</option>)}
          </select>
        </span>
      </div>

      <div className="set-hol-bar">
        <div className="form-field set-hol-date">
          <label className="lbl" htmlFor="hol-date">Tarih</label>
          <input id="hol-date" className="form-input" type="date" value={date} onChange={(e) => setDate(e.target.value)} />
        </div>
        <div className="form-field set-hol-type">
          <label className="lbl" htmlFor="hol-type">Tür</label>
          <select id="hol-type" className="form-select" value={isWorking ? "work" : "off"}
            onChange={(e) => setIsWorking(e.target.value === "work")}>
            <option value="off">Tatil (çalışılmaz)</option>
            <option value="work">Telafi (çalışılır)</option>
          </select>
        </div>
        <div className="form-field set-hol-note">
          <label className="lbl" htmlFor="hol-note">Açıklama</label>
          <input id="hol-note" className="form-input" value={note} onChange={(e) => setNote(e.target.value)}
            maxLength={200} placeholder="örn. Ramazan Bayramı" />
        </div>
        <button type="button" className="btn-md primary" disabled={!date || setMut.isPending}
          onClick={() => setMut.mutate({ date, isWorking, note: note.trim() || null })}>
          <Icon name="plus" size={14} />{setMut.isPending ? "Ekleniyor…" : "Ekle"}
        </button>
      </div>
      {setMut.error ? <div className="form-banner-err">{setMut.error.message}</div> : null}
      {/* Dinamik uyarı: `aria-live` olmadan ekran okuyucu bunu HİÇ duyurmaz.
          İkon + metin birlikte (renk tek başına anlatmaz). */}
      {redundant ? (
        <div className="set-hol-redundant" role="status" aria-live="polite">
          <Icon name="alertTriangle" size={12} />{redundant}
        </div>
      ) : null}

      {exQ.isError ? (
        // Hata durumunu boş listeye çevirmek KESİN bir olumsuz iddiaya dönüşür
        // ("tanımlı gün yok") ve kullanıcı mevcut tatili yeniden girmeye kalkar.
        <div className="form-banner-err">Tatil listesi yüklenemedi: {exQ.error.message}</div>
      ) : exQ.isLoading ? (
        <div className="empty-state"><p className="ttl">Yükleniyor…</p></div>
      ) : rows.length === 0 ? (
        <div className="empty-state">
          <p className="ttl">{year} için tanımlı gün yok</p>
          <p className="sub">
            Resmî tatiller ve telafi mesaileri buraya girilir. Proje planlaması (Gantt,
            kritik yol, kaynak yükü) bu günleri haftalık düzenin <b>üzerine</b> uygular.
          </p>
        </div>
      ) : (
        <table className="dt2">
          <thead>
            <tr><th>Tarih</th><th>Tür</th><th>Açıklama</th><th className="set-hol-act" /></tr>
          </thead>
          <tbody>
            {rows.map((r) => {
              const d = exDay(r.date);
              return (
                <tr key={r.id} className={d.key < todayKey ? "set-hol-past" : undefined}>
                  <td>
                    <span className="mono">{d.text}</span>
                    <span className="set-hol-dow">{DOW_TR[d.dow]}</span>
                  </td>
                  <td>
                    {/* Durum = renk + ikon + metin (CLAUDE.md kural 5) */}
                    {r.isWorking ? (
                      <span className="badge-pill b-info"><Icon name="clock" size={11} />Telafi (çalışılır)</span>
                    ) : (
                      <span className="badge-pill b-warn"><Icon name="calendar" size={11} />Tatil</span>
                    )}
                  </td>
                  <td className="set-hol-note-cell">{r.note || "—"}</td>
                  <td>
                    <button type="button" className="iact danger"
                      aria-label={`${d.text} — ${r.isWorking ? "telafi" : "tatil"} kaydını sil`}
                      title="Sil"
                      onClick={() => setDelId(r.id)}><Icon name="trash" size={13} /></button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      )}
      {delMut.error ? <div className="form-banner-err">{delMut.error.message}</div> : null}

      {delDialog}
    </div>
  );
}

/* --- Calisma Duzeni: calisma gunleri + vardiyalar ------------------ */
const DAY_LABELS: Array<{ day: number; label: string }> = [
  { day: 1, label: "Pzt" }, { day: 2, label: "Sal" }, { day: 3, label: "Çar" },
  { day: 4, label: "Per" }, { day: 5, label: "Cum" }, { day: 6, label: "Cmt" }, { day: 7, label: "Paz" },
];

function WorkScheduleSection() {
  const qc = useQueryClient();
  const isMobile = useIsMobile();
  const wsQ = trpc.settings.workSchedule.get.useQuery();
  const daysMut = trpc.settings.workSchedule.setWorkDays.useMutation({ onSuccess: () => qc.invalidateQueries() });
  // Günlük kapasite — kaynak yükü (Projeler > Kaynak) aşımı bunun üstünden hesaplanır.
  const hoursMut = trpc.settings.workSchedule.setHoursPerDay.useMutation({ onSuccess: () => qc.invalidateQueries() });
  const [hoursDraft, setHoursDraft] = useState<string | null>(null);
  const createMut = trpc.settings.workSchedule.createShift.useMutation({ onSuccess: () => { qc.invalidateQueries(); setNName(""); setNCode(""); setNStart(""); setNEnd(""); } });
  const updateMut = trpc.settings.workSchedule.updateShift.useMutation({ onSuccess: () => { qc.invalidateQueries(); setEditId(null); } });
  const toggleMut = trpc.settings.workSchedule.toggleShift.useMutation({ onSuccess: () => qc.invalidateQueries() });

  // Yeni vardiya formu
  const [nName, setNName] = useState("");
  const [nCode, setNCode] = useState("");
  const [nStart, setNStart] = useState("");
  const [nEnd, setNEnd] = useState("");
  const tp = useTesisPicker(true);
  const [tesisPick, setTesisPick] = useState("");
  // Satır-içi düzenleme
  const [editId, setEditId] = useState<string | null>(null);
  const [eName, setEName] = useState("");
  const [eCode, setECode] = useState("");
  const [eStart, setEStart] = useState("");
  const [eEnd, setEEnd] = useState("");

  const shifts = wsQ.data?.shifts ?? [];
  const workDays = wsQ.data?.workDays ?? [];

  function toggleDay(day: number) {
    const next = workDays.includes(day) ? workDays.filter((d) => d !== day) : [...workDays, day];
    daysMut.mutate({ workDays: next });
  }
  function addShift() {
    if (!nName.trim() || !nStart || !nEnd) return;
    if (tp.show && !tesisPick) return;
    createMut.mutate({ name: nName.trim(), code: nCode.trim(), startTime: nStart, endTime: nEnd, tesisId: resolveTesisId(tp.show, tesisPick) });
  }
  function startEdit(s: { id: string; name: string; code: string | null; startTime: string; endTime: string }) {
    setEditId(s.id); setEName(s.name); setECode(s.code ?? ""); setEStart(s.startTime); setEEnd(s.endTime); updateMut.reset();
  }
  function saveEdit() {
    if (!editId || !eName.trim() || !eStart || !eEnd) return;
    updateMut.mutate({ id: editId, name: eName.trim(), code: eCode.trim(), startTime: eStart, endTime: eEnd });
  }

  const overnight = (s: string, e: string) => s && e && e <= s; // bitiş ≤ başlangıç → gece yarısını aşar

  const introText = (
    <p className="sub" style={{ margin: "0 0 14px" }}>
      Firma geneli çalışma günleri ve vardiyalar. Parti üretim kayıtlarına, üretim saatine göre vardiya <b>otomatik</b> atanır.
    </p>
  );

  const capacityBlock = (
    <div style={{ marginBottom: 20 }}>
      <div style={{ fontSize: 12.5, fontWeight: 600, color: "var(--neutral-700)", marginBottom: 8 }}>Günlük Kapasite</div>
      <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
        <input
          className="form-input" style={{ width: 110 }} inputMode="decimal"
          aria-label="Günlük kapasite (saat)"
          value={hoursDraft ?? String(wsQ.data?.hoursPerDay ?? 8)}
          onChange={(e) => setHoursDraft(e.target.value)}
          disabled={hoursMut.isPending || wsQ.isLoading}
        />
        <span style={{ fontSize: 12.5, color: "var(--neutral-600)" }}>saat / gün</span>
        <button
          type="button" className="btn-md primary"
          disabled={hoursMut.isPending || hoursDraft === null}
          onClick={() => {
            const n = Number((hoursDraft ?? "").replace(",", "."));
            if (!Number.isFinite(n) || n < 0.5 || n > 24) return;
            hoursMut.mutate({ hoursPerDay: n }, { onSuccess: () => setHoursDraft(null) });
          }}
        >
          {hoursMut.isPending ? "Kaydediliyor…" : "Kaydet"}
        </button>
        {hoursMut.isError ? <span className="form-banner-err" style={{ margin: 0 }}>{hoursMut.error.message}</span> : null}
      </div>
      <p style={{ fontSize: 11.5, color: "var(--neutral-500)", margin: "6px 0 0" }}>
        Bir kişinin günde kaç saat çalıştığı. Projeler → <b>Kaynak</b> sekmesindeki aşırı yük
        uyarısı bu değerin üstünü aşım sayar. Örn. 7,5 saatlik vardiya için 7,5 girin.
      </p>
    </div>
  );

  const workDaysBlock = (
    <div style={{ marginBottom: 20 }}>
      <div style={{ fontSize: 12.5, fontWeight: 600, color: "var(--neutral-700)", marginBottom: 8 }}>Çalışma Günleri</div>
      <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
        {DAY_LABELS.map(({ day, label }) => {
          const on = workDays.includes(day);
          return (
            <button
              key={day} type="button" onClick={() => toggleDay(day)} disabled={daysMut.isPending || wsQ.isLoading}
              aria-pressed={on}
              style={{
                padding: "7px 14px", fontSize: 12.5, fontWeight: 600, cursor: "pointer",
                border: on ? "1px solid var(--primary)" : "1px solid var(--neutral-300)",
                borderRadius: "var(--radius-md)",
                background: on ? "var(--primary)" : "var(--white)",
                color: on ? "var(--primary-on)" : "var(--neutral-600)",
                transition: "background-color 150ms, border-color 150ms",
              }}
            >{label}</button>
          );
        })}
      </div>
      {daysMut.error ? <div style={{ fontSize: 11.5, color: "var(--danger)", marginTop: 6 }}>{daysMut.error.message}</div> : null}
    </div>
  );

  if (isMobile) {
    return (
      <>
        {introText}
        {capacityBlock}
        {workDaysBlock}
        <CalendarExceptionsSection workDays={workDays} />

        <div style={{ fontSize: 12.5, fontWeight: 600, color: "var(--neutral-700)" }}>Vardiyalar</div>
        <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
          <label>
            <span className="m-ovl" style={{ display: "block", marginBottom: 3 }}>Vardiya Adı</span>
            <input className="form-input" value={nName} onChange={(e) => setNName(e.target.value)} maxLength={100} placeholder="örn. 1. Vardiya" />
          </label>
          <label>
            <span className="m-ovl" style={{ display: "block", marginBottom: 3 }}>Kısa Ad</span>
            <input className="form-input mono" value={nCode} onChange={(e) => setNCode(e.target.value)} maxLength={8} placeholder="örn. G" />
          </label>
          <div style={{ display: "flex", gap: 8 }}>
            <label style={{ flex: 1 }}>
              <span className="m-ovl" style={{ display: "block", marginBottom: 3 }}>Başlangıç</span>
              <input className="form-input" type="time" value={nStart} onChange={(e) => setNStart(e.target.value)} />
            </label>
            <label style={{ flex: 1 }}>
              <span className="m-ovl" style={{ display: "block", marginBottom: 3 }}>Bitiş</span>
              <input className="form-input" type="time" value={nEnd} onChange={(e) => setNEnd(e.target.value)} />
            </label>
          </div>
          {tp.show ? <TesisPickerField show={tp.show} facilities={tp.facilities} value={tesisPick} onChange={setTesisPick} /> : null}
          <button type="button" className="btn-md primary" style={{ justifyContent: "center" }} onClick={addShift} disabled={createMut.isPending || !nName.trim() || !nStart || !nEnd || (tp.show && !tesisPick)}>
            <Icon name="plus" size={14} />Ekle
          </button>
        </div>
        {createMut.error ? <div className="form-banner-err">{createMut.error.message}</div> : null}
        {nStart && nEnd && overnight(nStart, nEnd) ? (
          <div style={{ fontSize: 11.5, color: "var(--info)" }}>Bitiş başlangıçtan önce — bu vardiya gece yarısını aşar (örn. {nStart} → ertesi gün {nEnd}).</div>
        ) : null}

        {wsQ.isLoading ? <div className="m-empty">Yükleniyor…</div> : shifts.length === 0 ? (
          <div className="m-empty">Henüz vardiya yok — ekleyin, üretim kayıtları saatine göre otomatik bağlanır.</div>
        ) : (
          shifts.map((s) => {
            const editing = editId === s.id;
            return (
              <MCard pad key={s.id} style={{ opacity: s.isActive ? 1 : 0.55 }}>
                {editing ? (
                  <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                    <input className="form-input" value={eName} onChange={(e) => setEName(e.target.value)} maxLength={100} aria-label="Vardiya adı" placeholder="Vardiya adı" />
                    <div style={{ display: "flex", gap: 8 }}>
                      <input className="form-input" type="time" value={eStart} onChange={(e) => setEStart(e.target.value)} aria-label="Başlangıç" style={{ flex: 1 }} />
                      <input className="form-input" type="time" value={eEnd} onChange={(e) => setEEnd(e.target.value)} aria-label="Bitiş" style={{ flex: 1 }} />
                    </div>
                    <div style={{ display: "flex", gap: 6, justifyContent: "flex-end" }}>
                      <button type="button" className="m-iconbtn m-li-act" style={{ color: "var(--success)" }} aria-label="Kaydet" onClick={saveEdit} disabled={updateMut.isPending}><Icon name="check" size={16} /></button>
                      <button type="button" className="m-iconbtn m-li-act" aria-label="Vazgeç" onClick={() => setEditId(null)} disabled={updateMut.isPending}><Icon name="x" size={16} /></button>
                    </div>
                  </div>
                ) : (
                  <>
                    <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                      <span style={{ flex: 1, minWidth: 0, fontSize: 13.5, fontWeight: 700, color: "var(--neutral-900)" }}>{s.name}</span>
                      {s.isActive ? <span className="badge b-success no-dot"><Icon name="check" size={11} />Aktif</span> : <span className="badge b-neutral no-dot">Pasif</span>}
                    </div>
                    <div style={{ display: "flex", alignItems: "center", gap: 8, marginTop: 8 }}>
                      <span className="mono" style={{ flex: 1, fontSize: 13, fontWeight: 600 }}>
                        {s.startTime} → {s.endTime}{overnight(s.startTime, s.endTime) ? <span style={{ color: "var(--neutral-400)", fontSize: 10.5 }}> +1g</span> : null}
                      </span>
                      <button type="button" className="m-iconbtn m-li-act" aria-label="Düzenle" onClick={() => startEdit(s)}><Icon name="edit" size={16} /></button>
                      <button type="button" className={`m-iconbtn m-li-act${s.isActive ? " danger" : ""}`} style={!s.isActive ? { color: "var(--success)" } : undefined} aria-label={s.isActive ? "Pasifleştir" : "Aktifleştir"} onClick={() => toggleMut.mutate({ id: s.id, isActive: !s.isActive })} disabled={toggleMut.isPending}><Icon name={s.isActive ? "archive" : "check"} size={16} /></button>
                    </div>
                  </>
                )}
              </MCard>
            );
          })
        )}
        {updateMut.error && editId ? <div style={{ fontSize: 11.5, color: "var(--danger)" }} role="alert">{updateMut.error.message}</div> : null}
      </>
    );
  }

  return (
    <div>
      {introText}

      {/* Günlük kapasite */}
      {capacityBlock}

      {/* Çalışma günleri */}
      {workDaysBlock}

      <CalendarExceptionsSection workDays={workDays} />

      {/* Vardiyalar */}
      <div style={{ fontSize: 12.5, fontWeight: 600, color: "var(--neutral-700)", marginBottom: 8 }}>Vardiyalar</div>
      <div style={{ display: "flex", gap: 8, alignItems: "flex-end", flexWrap: "wrap", marginBottom: 12 }}>
        <div className="form-field" style={{ margin: 0, width: 220 }}>
          <label className="lbl" htmlFor="ws-name">Vardiya Adı</label>
          <input id="ws-name" className="form-input" value={nName} onChange={(e) => setNName(e.target.value)} maxLength={100} placeholder="örn. 1. Vardiya" />
        </div>
        <div className="form-field" style={{ margin: 0, width: 118 }}>
          <label className="lbl" htmlFor="ws-code">Kısa Ad</label>
          <input id="ws-code" className="form-input mono" value={nCode} onChange={(e) => setNCode(e.target.value)} maxLength={8} placeholder="örn. G" />
          <span className="help">Excel plan şablonunda hücreye bu yazılır.</span>
        </div>
        <div className="form-field" style={{ margin: 0, width: 130 }}>
          <label className="lbl" htmlFor="ws-start">Başlangıç</label>
          <input id="ws-start" className="form-input" type="time" value={nStart} onChange={(e) => setNStart(e.target.value)} />
        </div>
        <div className="form-field" style={{ margin: 0, width: 130 }}>
          <label className="lbl" htmlFor="ws-end">Bitiş</label>
          <input id="ws-end" className="form-input" type="time" value={nEnd} onChange={(e) => setNEnd(e.target.value)} />
        </div>
        {tp.show ? <div style={{ margin: 0, minWidth: 180 }}><TesisPickerField show={tp.show} facilities={tp.facilities} value={tesisPick} onChange={setTesisPick} /></div> : null}
        <button type="button" className="btn-md primary" onClick={addShift} disabled={createMut.isPending || !nName.trim() || !nStart || !nEnd || (tp.show && !tesisPick)}>
          <Icon name="plus" size={14} />Ekle
        </button>
      </div>
      {createMut.error ? <div className="form-banner-err">{createMut.error.message}</div> : null}
      {nStart && nEnd && overnight(nStart, nEnd) ? (
        <div style={{ fontSize: 11.5, color: "var(--info)", marginTop: -6, marginBottom: 10 }}>Bitiş başlangıçtan önce — bu vardiya gece yarısını aşar (örn. {nStart} → ertesi gün {nEnd}).</div>
      ) : null}

      {wsQ.isLoading ? (
        <div className="empty-state"><p className="ttl">Yükleniyor…</p></div>
      ) : shifts.length === 0 ? (
        <div className="empty-state">
          <p className="ttl">Henüz vardiya yok</p>
          <p className="sub">Vardiya ekleyin; üretim kayıtları saatine göre otomatik vardiyaya bağlanır.</p>
        </div>
      ) : (
        <table className="dt2">
          <thead>
            <tr>
              <th>Vardiya</th>
              <th style={{ width: 118 }}>Kısa Ad</th>
              <th style={{ width: 130 }}>Başlangıç</th>
              <th style={{ width: 130 }}>Bitiş</th>
              <th style={{ width: 110 }}>Durum</th>
              <th style={{ width: 116 }}>Eylemler</th>
            </tr>
          </thead>
          <tbody>
            {shifts.map((s) => {
              const editing = editId === s.id;
              return (
                <tr key={s.id} style={{ opacity: s.isActive ? 1 : 0.5 }}>
                  <td>
                    {editing ? <input className="form-input" value={eName} onChange={(e) => setEName(e.target.value)} maxLength={100} aria-label="Vardiya adı" style={{ height: 30, fontSize: 12.5, maxWidth: 240 }} /> : s.name}
                  </td>
                  <td>
                    {editing
                      ? <input className="form-input" value={eCode} onChange={(e) => setECode(e.target.value)} maxLength={8} aria-label="Kısa ad" placeholder="G" style={{ height: 30, fontSize: 12.5, width: 96 }} />
                      : s.code ? <span className="badge-pill b-primary mono">{s.code}</span> : <span className="mono" style={{ color: "var(--neutral-400)", fontSize: 12 }}>—</span>}
                  </td>
                  <td>
                    {editing ? <input className="form-input" type="time" value={eStart} onChange={(e) => setEStart(e.target.value)} aria-label="Başlangıç" style={{ height: 30, fontSize: 12.5, width: 110 }} /> : <span className="mono" style={{ fontSize: 12.5 }}>{s.startTime}</span>}
                  </td>
                  <td>
                    {editing ? <input className="form-input" type="time" value={eEnd} onChange={(e) => setEEnd(e.target.value)} aria-label="Bitiş" style={{ height: 30, fontSize: 12.5, width: 110 }} /> : (
                      <span className="mono" style={{ fontSize: 12.5 }}>
                        {s.endTime}{overnight(s.startTime, s.endTime) ? <span style={{ color: "var(--neutral-400)", fontSize: 10.5 }}> +1g</span> : null}
                      </span>
                    )}
                  </td>
                  <td>{s.isActive ? <span className="badge-pill b-success">Aktif</span> : <span className="badge-pill">Pasif</span>}</td>
                  <td>
                    <div className="row-acts">
                      {editing ? (
                        <>
                          <button type="button" className="iact success" aria-label="Kaydet" title="Kaydet" onClick={saveEdit} disabled={updateMut.isPending}><Icon name="check" size={15} /></button>
                          <button type="button" className="iact" aria-label="Vazgeç" title="Vazgeç" onClick={() => setEditId(null)} disabled={updateMut.isPending}><Icon name="x" size={15} /></button>
                        </>
                      ) : (
                        <>
                          <button type="button" className="iact" aria-label="Düzenle" title="Düzenle" onClick={() => startEdit(s)}><Icon name="edit" size={15} /></button>
                          <ToggleBtn active={s.isActive} busy={toggleMut.isPending} onClick={() => toggleMut.mutate({ id: s.id, isActive: !s.isActive })} />
                        </>
                      )}
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      )}
      {updateMut.error && editId ? <div style={{ fontSize: 11.5, color: "var(--danger)", marginTop: 6 }} role="alert">{updateMut.error.message}</div> : null}
    </div>
  );
}

/* ─── Anlaşmalı Bankalar (satınalma ödeme — kredi kartı çoklu seçim) ─────────── */
function BanksSection() {
  const qc = useQueryClient();
  const isMobile = useIsMobile();
  const listQ = trpc.settings.bank.list.useQuery({ includeInactive: true });
  const createMut = trpc.settings.bank.create.useMutation({ onSuccess: () => qc.invalidateQueries() });
  const updateMut = trpc.settings.bank.update.useMutation({ onSuccess: () => qc.invalidateQueries() });
  const toggleMut = trpc.settings.bank.toggle.useMutation({ onSuccess: () => qc.invalidateQueries() });

  const [newName, setNewName] = useState("");
  const [editId, setEditId] = useState<string | null>(null);
  const [editName, setEditName] = useState("");
  const [err, setErr] = useState<string | null>(null);

  const rows = listQ.data ?? [];

  function add() {
    setErr(null);
    const name = newName.trim();
    if (!name) return setErr("Banka adı girin.");
    createMut.mutate({ name }, { onSuccess: () => setNewName(""), onError: (e) => setErr(e.message) });
  }
  function saveEdit() {
    if (!editId) return;
    const name = editName.trim();
    if (!name) return;
    updateMut.mutate({ id: editId, name }, { onSuccess: () => { setEditId(null); setEditName(""); } });
  }

  if (isMobile) {
    return (
      <>
        <div style={{ display: "flex", gap: 8 }}>
          <input className="form-input" style={{ flex: 1 }} value={newName} onChange={(e) => setNewName(e.target.value)} placeholder="Yeni banka adı" maxLength={100} onKeyDown={(e) => { if (e.key === "Enter") add(); }} aria-label="Yeni banka adı" />
          <button type="button" className="btn-md primary" onClick={add} disabled={createMut.isPending}><Icon name="plus" size={14} />Ekle</button>
        </div>
        {err ? <div className="form-banner-err">{err}</div> : null}
        {listQ.isLoading ? <div className="m-empty">Yükleniyor…</div> : rows.length === 0 ? (
          <div className="m-empty">Henüz banka yok — ilk anlaşmalı bankayı ekleyin.</div>
        ) : (
          rows.map((b) => (
            <MCard pad key={b.id}>
              {editId === b.id ? (
                <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
                  <input className="form-input" style={{ flex: 1 }} value={editName} onChange={(e) => setEditName(e.target.value)} maxLength={100} autoFocus onKeyDown={(e) => { if (e.key === "Enter") saveEdit(); if (e.key === "Escape") setEditId(null); }} aria-label="Banka adı" />
                  <button type="button" className="m-iconbtn m-li-act" style={{ color: "var(--success)" }} aria-label="Kaydet" onClick={saveEdit} disabled={updateMut.isPending}><Icon name="check" size={16} /></button>
                  <button type="button" className="m-iconbtn m-li-act" aria-label="Vazgeç" onClick={() => setEditId(null)}><Icon name="x" size={16} /></button>
                </div>
              ) : (
                <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                  <span style={{ flex: 1, minWidth: 0, fontSize: 13.5, fontWeight: 600, color: "var(--neutral-900)", opacity: b.isActive ? 1 : 0.5 }}>{b.name}</span>
                  {b.isActive ? <span className="badge b-success no-dot"><Icon name="check" size={11} />Aktif</span> : <span className="badge b-neutral no-dot">Pasif</span>}
                  <button type="button" className="m-iconbtn m-li-act" aria-label="Düzenle" onClick={() => { setEditId(b.id); setEditName(b.name); }}><Icon name="edit" size={16} /></button>
                  <button type="button" className={`m-iconbtn m-li-act${b.isActive ? " danger" : ""}`} style={!b.isActive ? { color: "var(--success)" } : undefined} aria-label={b.isActive ? "Pasifleştir" : "Aktifleştir"} onClick={() => toggleMut.mutate({ id: b.id, isActive: !b.isActive })} disabled={toggleMut.isPending}><Icon name={b.isActive ? "archive" : "check"} size={16} /></button>
                </div>
              )}
            </MCard>
          ))
        )}
      </>
    );
  }

  return (
    <div>
      <div style={{ display: "flex", alignItems: "center", marginBottom: 14, gap: 12 }}>
        <p className="sub" style={{ margin: 0 }}>Anlaşmalı bankalar — satınalma ödemesinde "Kredi Kartı" seçilince listeden çoklu seçilir.</p>
        <span style={{ flex: 1 }} />
        <input className="form-input" style={{ maxWidth: 240 }} value={newName} onChange={(e) => setNewName(e.target.value)} placeholder="Yeni banka adı" maxLength={100} onKeyDown={(e) => { if (e.key === "Enter") add(); }} />
        <button type="button" className="btn-md primary" onClick={add} disabled={createMut.isPending}><Icon name="plus" size={14} />Ekle</button>
      </div>
      {err ? <div className="form-banner-err" style={{ marginBottom: 10 }}>{err}</div> : null}

      {listQ.isLoading ? (
        <div className="empty-state"><p className="ttl">Yükleniyor…</p></div>
      ) : rows.length === 0 ? (
        <div className="empty-state"><p className="ttl">Henüz banka yok</p><p className="sub">İlk anlaşmalı bankayı ekleyin; satınalma ödeme yönteminde listelenir.</p></div>
      ) : (
        <table className="dt2">
          <thead><tr><th>Banka</th><th style={{ width: 90 }}>Durum</th><th style={{ width: 116 }}>Eylemler</th></tr></thead>
          <tbody>
            {rows.map((b) => (
              <tr key={b.id}>
                <td>
                  {editId === b.id ? (
                    <input className="form-input" style={{ maxWidth: 280 }} value={editName} onChange={(e) => setEditName(e.target.value)} maxLength={100} autoFocus onKeyDown={(e) => { if (e.key === "Enter") saveEdit(); if (e.key === "Escape") setEditId(null); }} />
                  ) : b.name}
                </td>
                <td>{b.isActive ? <span className="badge-pill b-success">Aktif</span> : <span className="badge-pill">Pasif</span>}</td>
                <td>
                  <div className="row-acts">
                    {editId === b.id ? (
                      <>
                        <button type="button" className="iact success" aria-label="Kaydet" title="Kaydet" onClick={saveEdit} disabled={updateMut.isPending}><Icon name="check" size={15} /></button>
                        <button type="button" className="iact" aria-label="Vazgeç" title="Vazgeç" onClick={() => setEditId(null)}><Icon name="x" size={15} /></button>
                      </>
                    ) : (
                      <>
                        <button type="button" className="iact" aria-label="Düzenle" title="Düzenle" onClick={() => { setEditId(b.id); setEditName(b.name); }}><Icon name="edit" size={15} /></button>
                        <ToggleBtn active={b.isActive} busy={toggleMut.isPending} onClick={() => toggleMut.mutate({ id: b.id, isActive: !b.isActive })} />
                      </>
                    )}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}

/* ─── Firma Bilgileri (Ship-From — Yükleme Formu başlığı) ─────────── */
function CompanyProfileSection() {
  const qc = useQueryClient();
  const isMobile = useIsMobile();
  const meQ = trpc.auth.me.useQuery(undefined, { staleTime: 5 * 60_000 });
  const facilities = (meQ.data?.user as { facilities?: Array<{ id: string; code: string; name: string }> } | undefined)?.facilities ?? [];
  const activeFacilityId = (meQ.data?.user as { activeFacilityId?: string | null } | undefined)?.activeFacilityId ?? null;
  const [tesisId, setTesisId] = useState<string | null>(null);
  // Seçili tesis: aktif tesis → yoksa ilk tesis (süper-admin çok tesisliyse seçici görünür).
  useEffect(() => { if (!tesisId && facilities.length) setTesisId(activeFacilityId ?? facilities[0]!.id); }, [facilities, activeFacilityId, tesisId]);

  const profQ = trpc.settings.companyProfile.get.useQuery({ tesisId: tesisId ?? undefined }, { enabled: !!tesisId });
  const upsertMut = trpc.settings.companyProfile.upsert.useMutation();
  const uploadMut = trpc.settings.companyProfile.uploadLogo.useMutation();
  const removeLogoMut = trpc.settings.companyProfile.removeLogo.useMutation();
  const [f, setF] = useState({ name: "", address: "", taxOffice: "", taxNumber: "", phone: "", email: "" });
  const [ok, setOk] = useState(false);
  const [dirty, setDirty] = useState(false); // kullanıcı yazarken refetch (örn. logo yükleme) formu ezmesin

  useEffect(() => {
    const d = profQ.data;
    if (d && !dirty) setF({ name: d.name ?? "", address: d.address ?? "", taxOffice: d.taxOffice ?? "", taxNumber: d.taxNumber ?? "", phone: d.phone ?? "", email: d.email ?? "" });
  }, [profQ.data, dirty]);
  // Tesis değişince formu temizle (yeni tesisin değerleri yüklensin).
  useEffect(() => { setDirty(false); setOk(false); }, [tesisId]);

  const set = (k: keyof typeof f, v: string) => { setF((s) => ({ ...s, [k]: v })); setOk(false); setDirty(true); };
  const logoUrl = profQ.data?.logoUrl ?? null;

  async function save() {
    setOk(false);
    try {
      await upsertMut.mutateAsync({
        tesisId: tesisId ?? undefined,
        name: f.name.trim(),
        address: f.address.trim() || null, taxOffice: f.taxOffice.trim() || null,
        taxNumber: f.taxNumber.trim() || null, phone: f.phone.trim() || null, email: f.email.trim() || null,
      });
      await qc.invalidateQueries();
      setDirty(false); // kaydedildi → sunucu değerleri tekrar yüklenebilir
      setOk(true);
    } catch { /* upsertMut.error gösterilir */ }
  }
  function onLogo(e: ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0]; e.target.value = "";
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => uploadMut.mutate({ tesisId: tesisId ?? undefined, filename: file.name, dataBase64: reader.result as string }, { onSuccess: () => qc.invalidateQueries() });
    reader.readAsDataURL(file);
  }

  const content = (
    <>
      <p className="sub" style={{ marginTop: 0, marginBottom: 14 }}>Sevkiyat "Yükleme Formu", irsaliye ve tüm form başlıklarında basılan <b>tesise ait</b> gönderici (Ship-From) firma bilgileri.</p>
      {facilities.length > 1 ? (
        <div className="form-field" style={{ maxWidth: 360 }}>
          <label className="lbl" htmlFor="cp-tesis">Tesis</label>
          <select id="cp-tesis" className="form-select" value={tesisId ?? ""} onChange={(e) => setTesisId(e.target.value)}>
            {facilities.map((t) => <option key={t.id} value={t.id}>{t.name} ({t.code})</option>)}
          </select>
          <span className="help">Her tesisin kendi künyesi ve logosu ayrı tutulur.</span>
        </div>
      ) : null}
      {upsertMut.error ? <div className="form-banner-err">{upsertMut.error.message}</div> : null}
      {ok ? <div className="form-banner-ok">Firma bilgileri kaydedildi.</div> : null}

      <div className="form-field">
        <label className="lbl">Logo</label>
        <div style={{ display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap" }}>
          {logoUrl
            ? <img src={logoUrl} alt="Firma logosu" style={{ height: 56, maxWidth: 180, objectFit: "contain", border: "1px solid var(--neutral-200)", borderRadius: "var(--radius-md)", padding: 4, background: "var(--white)" }} />
            : <div style={{ height: 56, width: 90, border: "1px dashed var(--neutral-300)", borderRadius: "var(--radius-md)", display: "flex", alignItems: "center", justifyContent: "center", color: "var(--neutral-400)", fontSize: 11 }}>Logo yok</div>}
          <label className="btn-md secondary" style={{ cursor: uploadMut.isPending ? "wait" : "pointer" }}>
            <Icon name="upload" size={14} />{uploadMut.isPending ? "Yükleniyor…" : "Logo Yükle"}
            <input type="file" accept="image/png,image/jpeg,image/webp,image/gif" hidden onChange={onLogo} disabled={uploadMut.isPending} />
          </label>
          {logoUrl ? <button type="button" className="btn-md ghost" style={{ color: "var(--danger)" }} disabled={removeLogoMut.isPending} onClick={() => removeLogoMut.mutate({ tesisId: tesisId ?? undefined }, { onSuccess: () => qc.invalidateQueries() })}><Icon name="trash" size={14} />Kaldır</button> : null}
        </div>
        {uploadMut.error ? <span className="help" style={{ color: "var(--danger)" }}>{uploadMut.error.message}</span> : null}
      </div>

      <div className="form-row">
        <div className="form-field">
          <label className="lbl" htmlFor="cp-name">Unvan <span className="req">*</span></label>
          <input id="cp-name" className="form-input" value={f.name} onChange={(e) => set("name", e.target.value)} maxLength={200} placeholder="MAYA FOOD GIDA SAN. LTD. ŞTİ." />
        </div>
        <div className="form-field">
          <label className="lbl" htmlFor="cp-phone">Telefon</label>
          <input id="cp-phone" className="form-input" value={f.phone} onChange={(e) => set("phone", e.target.value)} maxLength={50} inputMode="tel" />
        </div>
      </div>
      <div className="form-row">
        <div className="form-field">
          <label className="lbl" htmlFor="cp-vd">Vergi Dairesi</label>
          <input id="cp-vd" className="form-input" value={f.taxOffice} onChange={(e) => set("taxOffice", e.target.value)} maxLength={120} />
        </div>
        <div className="form-field">
          <label className="lbl" htmlFor="cp-vn">Vergi Kimlik No</label>
          <input id="cp-vn" className="form-input" value={f.taxNumber} onChange={(e) => set("taxNumber", e.target.value)} maxLength={50} inputMode="numeric" />
        </div>
      </div>
      <div className="form-field">
        <label className="lbl" htmlFor="cp-email">E-posta</label>
        <input id="cp-email" className="form-input" value={f.email} onChange={(e) => set("email", e.target.value)} maxLength={120} inputMode="email" />
      </div>
      <div className="form-field">
        <label className="lbl" htmlFor="cp-addr">Adres</label>
        <textarea id="cp-addr" className="form-input" value={f.address} onChange={(e) => set("address", e.target.value)} maxLength={500} rows={2} style={{ resize: "vertical" }} />
      </div>

      <div style={{ marginTop: 8 }}>
        <button type="button" className="btn-md primary" disabled={upsertMut.isPending || !f.name.trim()} onClick={save}>{upsertMut.isPending ? "Kaydediliyor…" : "Kaydet"}</button>
      </div>
    </>
  );

  if (isMobile) return <div className="set-mform">{content}</div>;
  return <div style={{ maxWidth: 720 }}>{content}</div>;
}

/* ─── Alerjenler ─────────────────────────────────────────── */
function AllergensSection() {
  const qc = useQueryClient();
  const isMobile = useIsMobile();
  const listQ = trpc.settings.allergen.list.useQuery({ includeInactive: true });
  const createMut = trpc.settings.allergen.create.useMutation({ onSuccess: () => { qc.invalidateQueries(); setName(""); } });
  const toggleMut = trpc.settings.allergen.toggle.useMutation({ onSuccess: () => qc.invalidateQueries() });
  const updateMut = trpc.settings.allergen.update.useMutation({ onSuccess: () => { qc.invalidateQueries(); setEditingId(null); } });
  const [name, setName] = useState("");
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editName, setEditName] = useState("");
  const rows = listQ.data ?? [];

  function startEdit(id: string, current: string) {
    updateMut.reset();
    setEditingId(id);
    setEditName(current);
  }
  function saveEdit(id: string) {
    const n = editName.trim();
    if (!n) return;
    updateMut.mutate({ id, name: n });
  }

  if (isMobile) {
    return (
      <>
        <form onSubmit={(e) => { e.preventDefault(); if (name.trim()) createMut.mutate({ name: name.trim() }); }} style={{ display: "flex", gap: 8 }}>
          <input className="form-input" style={{ flex: 1 }} placeholder="Yeni alerjen (ör. gluten, soya)" value={name} onChange={(e) => setName(e.target.value)} maxLength={100} aria-label="Yeni alerjen" />
          <button type="submit" className="btn-md primary" disabled={!name.trim() || createMut.isPending}><Icon name="plus" size={14} />Ekle</button>
        </form>
        {createMut.error ? <div className="form-banner-err">{createMut.error.message}</div> : null}
        {updateMut.error ? <div className="form-banner-err">{updateMut.error.message}</div> : null}
        {rows.length === 0 ? <div className="m-empty">Henüz alerjen eklenmedi.</div> : (
          rows.map((a) => (
            <MCard pad key={a.id}>
              {editingId === a.id ? (
                <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
                  <input className="form-input" style={{ flex: 1 }} value={editName} autoFocus maxLength={100} onChange={(e) => setEditName(e.target.value)} onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); saveEdit(a.id); } if (e.key === "Escape") setEditingId(null); }} aria-label="Alerjen adı" />
                  <button type="button" className="m-iconbtn m-li-act" style={{ color: "var(--success)" }} aria-label="Kaydet" onClick={() => saveEdit(a.id)} disabled={!editName.trim() || updateMut.isPending}><Icon name="check" size={16} /></button>
                  <button type="button" className="m-iconbtn m-li-act" aria-label="Vazgeç" onClick={() => setEditingId(null)} disabled={updateMut.isPending}><Icon name="x" size={16} /></button>
                </div>
              ) : (
                <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                  <span style={{ flex: 1, minWidth: 0, fontSize: 13.5, fontWeight: 600, color: "var(--neutral-900)", opacity: a.isActive ? 1 : 0.5 }}>{a.name}</span>
                  {!a.isActive ? <span className="badge b-neutral no-dot">Pasif</span> : null}
                  <button type="button" className="m-iconbtn m-li-act" aria-label="Düzenle" onClick={() => startEdit(a.id, a.name)} disabled={editingId !== null}><Icon name="edit" size={16} /></button>
                  <button type="button" className={`m-iconbtn m-li-act${a.isActive ? " danger" : ""}`} style={!a.isActive ? { color: "var(--success)" } : undefined} aria-label={a.isActive ? "Pasifleştir" : "Aktifleştir"} onClick={() => toggleMut.mutate({ id: a.id, isActive: !a.isActive })} disabled={toggleMut.isPending}><Icon name={a.isActive ? "archive" : "check"} size={16} /></button>
                </div>
              )}
            </MCard>
          ))
        )}
      </>
    );
  }

  return (
    <section className="d-card">
      <div className="d-card-head">
        <div className="left"><span className="t">Alerjenler</span><span className="s">{rows.length} kayıt</span></div>
      </div>
      <div className="d-card-body">
        <form
          onSubmit={(e) => { e.preventDefault(); if (name.trim()) createMut.mutate({ name: name.trim() }); }}
          style={{ display: "flex", gap: 8, marginBottom: 14 }}
        >
          <input className="form-input" placeholder="Yeni alerjen (ör. gluten, soya, süt)" value={name} onChange={(e) => setName(e.target.value)} maxLength={100} style={{ maxWidth: 360 }} />
          <button type="submit" className="btn-md primary" disabled={!name.trim() || createMut.isPending}><Icon name="plus" size={14} />Ekle</button>
        </form>
        {createMut.error ? <div className="form-banner-err">{createMut.error.message}</div> : null}
        {updateMut.error ? <div className="form-banner-err">{updateMut.error.message}</div> : null}

        {rows.length === 0 ? (
          <div className="d-empty"><Icon name="alertTriangle" size={24} /><span className="m">Henüz alerjen eklenmedi.</span></div>
        ) : (
          <div className="list-card" style={{ border: "1px solid var(--neutral-200)", borderRadius: "var(--radius-md)" }}>
            {rows.map((a) =>
              editingId === a.id ? (
                <div key={a.id} className="list-item">
                  <span className="body">
                    <input
                      className="form-input"
                      value={editName}
                      autoFocus
                      maxLength={100}
                      onChange={(e) => setEditName(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === "Enter") { e.preventDefault(); saveEdit(a.id); }
                        if (e.key === "Escape") setEditingId(null);
                      }}
                      style={{ height: 30, maxWidth: 320 }}
                      aria-label="Alerjen adı"
                    />
                  </span>
                  <div className="row-acts">
                    <button type="button" className="iact success" aria-label="Kaydet" title="Kaydet" onClick={() => saveEdit(a.id)} disabled={!editName.trim() || updateMut.isPending}>
                      <Icon name="check" size={15} />
                    </button>
                    <button type="button" className="iact" aria-label="Vazgeç" title="Vazgeç" onClick={() => setEditingId(null)} disabled={updateMut.isPending}>
                      <Icon name="x" size={15} />
                    </button>
                  </div>
                </div>
              ) : (
                <div key={a.id} className="list-item">
                  <span className="body"><span className="t" style={{ opacity: a.isActive ? 1 : 0.5 }}>{a.name}</span></span>
                  {!a.isActive ? <span className="mono" style={{ fontSize: 10.5, color: "var(--neutral-400)" }}>pasif</span> : null}
                  <div className="row-acts">
                    <button type="button" className="iact" aria-label="Düzenle" title="Düzenle" onClick={() => startEdit(a.id, a.name)} disabled={editingId !== null}>
                      <Icon name="edit" size={15} />
                    </button>
                    <ToggleBtn active={a.isActive} busy={toggleMut.isPending} onClick={() => toggleMut.mutate({ id: a.id, isActive: !a.isActive })} />
                  </div>
                </div>
              )
            )}
          </div>
        )}
      </div>
    </section>
  );
}

/* ─── Kart Türleri ───────────────────────────────────────── */
function ItemTypesSection() {
  const qc = useQueryClient();
  const isMobile = useIsMobile();
  const [kind, setKind] = useState<ItemKind>("stock");
  const listQ = trpc.settings.itemType.list.useQuery({ kind, includeInactive: true });
  const createMut = trpc.settings.itemType.create.useMutation({ onSuccess: () => { qc.invalidateQueries(); setCode(""); setName(""); } });
  const toggleMut = trpc.settings.itemType.toggle.useMutation({ onSuccess: () => qc.invalidateQueries() });
  const updateMut = trpc.settings.itemType.update.useMutation({ onSuccess: () => { qc.invalidateQueries(); setEditingId(null); } });
  // "MRP dışı" anahtarı ayrı mutation: ad düzenlemesinin durumunu/hatasını ezmesin. onSuccess promise'i
  // döndürülür → liste yeniden gelene kadar isPending sürer, anahtar eski değere geri sıçramaz.
  const mrpMut = trpc.settings.itemType.update.useMutation({ onSuccess: () => qc.invalidateQueries() });
  const [code, setCode] = useState("");
  const [name, setName] = useState("");
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editName, setEditName] = useState("");
  const tp = useTesisPicker(true);
  const [tesisPick, setTesisPick] = useState("");
  const rows = listQ.data ?? [];

  function switchKind(k: ItemKind) {
    setEditingId(null);
    mrpMut.reset();
    setKind(k);
  }
  /** İstek sürerken satır istenen değeri gösterir; hata olursa sunucudaki değere döner. */
  function mrpExcluded(t: { id: string; excludeFromMrp: boolean }) {
    return mrpMut.isPending && mrpMut.variables?.id === t.id ? !!mrpMut.variables.excludeFromMrp : t.excludeFromMrp;
  }
  function setMrpExcluded(id: string, excludeFromMrp: boolean) {
    mrpMut.mutate({ id, excludeFromMrp });
  }
  const MRP_HINT = "Bu türdeki kalemler MRP satın alma eksiğine katılmaz, raporda ayrıca listelenir.";
  /* KOD ÖNEKİ KURALI GÖRÜNÜR — kural sunucuda (item-code.ts kodOneki); burada yalnız açıklanır.
     Mamulde tür kodu koda yansımaz (müşteri kararı 13.10.2026); ham madde / yarı mamulde önek olur. */
  const CODE_HINT = kind === "finished"
    ? "Mamul kodları her zaman MM- ile başlar; tür kodu koda yansımaz. Mevcut kart kodları değişmez."
    : "Tür kodu yeni kartların kod öneki olur; mevcut kart kodları değişmez.";
  function startEdit(id: string, current: string) {
    updateMut.reset();
    setEditingId(id);
    setEditName(current);
  }
  function saveEdit(id: string) {
    const n = editName.trim();
    if (!n) return;
    updateMut.mutate({ id, name: n });
  }

  if (isMobile) {
    return (
      <>
        <div className="m-chips">
          {(["stock", "semi", "finished"] as ItemKind[]).map((k) => (
            <button key={k} type="button" className={`m-chip${kind === k ? " act" : ""}`} onClick={() => switchKind(k)}>{KIND_LABEL[k]}</button>
          ))}
        </div>
        {tp.show ? <TesisPickerField show={tp.show} facilities={tp.facilities} value={tesisPick} onChange={setTesisPick} /> : null}
        <form
          onSubmit={(e) => { e.preventDefault(); if (tp.show && !tesisPick) return; if (code.trim() && name.trim()) createMut.mutate({ kind, code: code.trim().toUpperCase(), name: name.trim(), tesisId: resolveTesisId(tp.show, tesisPick) }); }}
          style={{ display: "flex", flexDirection: "column", gap: 8 }}
        >
          <input className="form-input" placeholder="Kod (ör. AMBALAJ)" value={code} onChange={(e) => setCode(e.target.value.toUpperCase())} maxLength={50} aria-label="Kod" />
          <div style={{ display: "flex", gap: 8 }}>
            <input className="form-input" style={{ flex: 1 }} placeholder="Ad (ör. Ambalaj Malzemesi)" value={name} onChange={(e) => setName(e.target.value)} maxLength={100} aria-label="Ad" />
            <button type="submit" className="btn-md primary" disabled={!code.trim() || !name.trim() || createMut.isPending}><Icon name="plus" size={14} />Ekle</button>
          </div>
        </form>
        {createMut.error ? <div className="form-banner-err">{createMut.error.message}</div> : null}
        {updateMut.error ? <div className="form-banner-err">{updateMut.error.message}</div> : null}
        {mrpMut.error ? <div className="form-banner-err" role="alert">{mrpMut.error.message}</div> : null}
        <p className="m-help set-it-mhint"><Icon name="hash" size={13} /><span><b>Kart kodu:</b> {CODE_HINT}</span></p>
        {kind === "stock" ? <p className="m-help set-it-mhint"><Icon name="info" size={13} /><span><b>MRP dışı (gider/hizmet):</b> {MRP_HINT}</span></p> : null}
        {rows.length === 0 ? <div className="m-empty">{KIND_LABEL[kind]} için tür eklenmedi.</div> : (
          rows.map((t) => (
            <MCard pad key={t.id}>
              {editingId === t.id ? (
                <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
                  <span className="mono" style={{ fontSize: 11.5, color: "var(--neutral-500)" }}>{t.code}</span>
                  <input className="form-input" style={{ flex: 1 }} value={editName} autoFocus maxLength={100} onChange={(e) => setEditName(e.target.value)} onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); saveEdit(t.id); } if (e.key === "Escape") setEditingId(null); }} aria-label="Tür adı" />
                  <button type="button" className="m-iconbtn m-li-act" style={{ color: "var(--success)" }} aria-label="Kaydet" onClick={() => saveEdit(t.id)} disabled={!editName.trim() || updateMut.isPending}><Icon name="check" size={16} /></button>
                  <button type="button" className="m-iconbtn m-li-act" aria-label="Vazgeç" onClick={() => setEditingId(null)} disabled={updateMut.isPending}><Icon name="x" size={16} /></button>
                </div>
              ) : (
                <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                  <span style={{ flex: 1, minWidth: 0, opacity: t.isActive ? 1 : 0.5 }}>
                    <span className="mono" style={{ fontSize: 11.5, color: "var(--neutral-500)", marginRight: 8 }}>{t.code}</span>
                    <span style={{ fontSize: 13.5, fontWeight: 600, color: "var(--neutral-900)" }}>{t.name}</span>
                  </span>
                  {!t.isActive ? <span className="badge b-neutral no-dot">Pasif</span> : null}
                  <button type="button" className="m-iconbtn m-li-act" aria-label="Düzenle" onClick={() => startEdit(t.id, t.name)} disabled={editingId !== null}><Icon name="edit" size={16} /></button>
                  <button type="button" className={`m-iconbtn m-li-act${t.isActive ? " danger" : ""}`} style={!t.isActive ? { color: "var(--success)" } : undefined} aria-label={t.isActive ? "Pasifleştir" : "Aktifleştir"} onClick={() => toggleMut.mutate({ id: t.id, isActive: !t.isActive })} disabled={toggleMut.isPending}><Icon name={t.isActive ? "archive" : "check"} size={16} /></button>
                </div>
              )}
              {/* Yalnız ham madde (stok) türü — MRP diğer türleri satın alma eksiği olarak hiç hesaplamaz. */}
              {t.kind === "stock" && editingId !== t.id ? (
                <div className="set-it-mrow">
                  <ToggleSwitch
                    checked={mrpExcluded(t)}
                    disabled={mrpMut.isPending}
                    onChange={(v) => setMrpExcluded(t.id, v)}
                    label="MRP dışı (gider/hizmet)"
                    ariaLabel={`${t.code} ${t.name} — MRP dışı (gider/hizmet)`}
                  />
                  {mrpExcluded(t) ? <span className="badge b-info no-dot"><Icon name="banknote" size={11} />MRP dışı</span> : null}
                </div>
              ) : null}
            </MCard>
          ))
        )}
      </>
    );
  }

  return (
    <section className="d-card">
      <div className="d-card-head">
        <div className="left"><span className="t">Kart Türleri</span><span className="s">{KIND_LABEL[kind]} · {rows.length} tür</span></div>
        <div style={{ display: "inline-flex", gap: 4 }}>
          {(["stock", "semi", "finished"] as ItemKind[]).map((k) => (
            <button key={k} type="button" className={`btn-md ${kind === k ? "primary" : "ghost"}`} style={{ height: 28, padding: "0 10px", fontSize: 12 }} onClick={() => switchKind(k)}>{KIND_LABEL[k]}</button>
          ))}
        </div>
      </div>
      <div className="d-card-body">
        {tp.show ? <div style={{ marginBottom: 10, maxWidth: 320 }}><TesisPickerField show={tp.show} facilities={tp.facilities} value={tesisPick} onChange={setTesisPick} /></div> : null}
        <form
          onSubmit={(e) => { e.preventDefault(); if (tp.show && !tesisPick) return; if (code.trim() && name.trim()) createMut.mutate({ kind, code: code.trim().toUpperCase(), name: name.trim(), tesisId: resolveTesisId(tp.show, tesisPick) }); }}
          style={{ display: "flex", gap: 8, marginBottom: 14, flexWrap: "wrap" }}
        >
          <input className="form-input" placeholder="Kod (ör. AMBALAJ)" value={code} onChange={(e) => setCode(e.target.value.toUpperCase())} maxLength={50} style={{ maxWidth: 200 }} />
          <input className="form-input" placeholder="Ad (ör. Ambalaj Malzemesi)" value={name} onChange={(e) => setName(e.target.value)} maxLength={100} style={{ maxWidth: 280 }} />
          <button type="submit" className="btn-md primary" disabled={!code.trim() || !name.trim() || createMut.isPending}><Icon name="plus" size={14} />Ekle</button>
        </form>
        {createMut.error ? <div className="form-banner-err">{createMut.error.message}</div> : null}
        {updateMut.error ? <div className="form-banner-err">{updateMut.error.message}</div> : null}
        {mrpMut.error ? <div className="form-banner-err" role="alert">{mrpMut.error.message}</div> : null}
        <p className="set-hint set-it-hint"><Icon name="hash" size={13} /><span><b>Kart kodu:</b> {CODE_HINT}</span></p>
        {kind === "stock" ? <p className="set-hint set-it-hint"><Icon name="info" size={13} /><span><b>MRP dışı (gider/hizmet):</b> {MRP_HINT}</span></p> : null}

        {rows.length === 0 ? (
          <div className="d-empty"><Icon name="list2" size={24} /><span className="m">{KIND_LABEL[kind]} için tür eklenmedi.</span></div>
        ) : (
          <div className="list-card" style={{ border: "1px solid var(--neutral-200)", borderRadius: "var(--radius-md)" }}>
            {rows.map((t) =>
              editingId === t.id ? (
                <div key={t.id} className="list-item">
                  <span className="body" style={{ flexDirection: "row", alignItems: "center", gap: 8 }}>
                    <span className="mono" style={{ fontSize: 11.5, color: "var(--neutral-500)" }}>{t.code}</span>
                    <input
                      className="form-input"
                      value={editName}
                      autoFocus
                      maxLength={100}
                      onChange={(e) => setEditName(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === "Enter") { e.preventDefault(); saveEdit(t.id); }
                        if (e.key === "Escape") setEditingId(null);
                      }}
                      style={{ height: 30, maxWidth: 280 }}
                      aria-label="Tür adı"
                    />
                  </span>
                  <div className="row-acts">
                    <button type="button" className="iact success" aria-label="Kaydet" title="Kaydet" onClick={() => saveEdit(t.id)} disabled={!editName.trim() || updateMut.isPending}>
                      <Icon name="check" size={15} />
                    </button>
                    <button type="button" className="iact" aria-label="Vazgeç" title="Vazgeç" onClick={() => setEditingId(null)} disabled={updateMut.isPending}>
                      <Icon name="x" size={15} />
                    </button>
                  </div>
                </div>
              ) : (
                <div key={t.id} className="list-item">
                  <span className="body" style={{ opacity: t.isActive ? 1 : 0.5 }}>
                    <span className="t"><span className="mono" style={{ fontSize: 11.5, color: "var(--neutral-500)", marginRight: 8 }}>{t.code}</span>{t.name}</span>
                  </span>
                  {t.kind === "stock" && mrpExcluded(t) ? <span className="badge-pill b-info"><Icon name="banknote" size={11} />MRP dışı</span> : null}
                  {!t.isActive ? <span className="mono" style={{ fontSize: 10.5, color: "var(--neutral-400)" }}>pasif</span> : null}
                  {/* Yalnız ham madde (stok) türü — MRP diğer türleri satın alma eksiği olarak hiç hesaplamaz. */}
                  {t.kind === "stock" ? (
                    <span className="set-it-mrp">
                      <ToggleSwitch
                        checked={mrpExcluded(t)}
                        disabled={mrpMut.isPending}
                        onChange={(v) => setMrpExcluded(t.id, v)}
                        label="MRP dışı (gider/hizmet)"
                        ariaLabel={`${t.code} ${t.name} — MRP dışı (gider/hizmet)`}
                      />
                    </span>
                  ) : null}
                  <div className="row-acts">
                    <button type="button" className="iact" aria-label="Düzenle" title="Düzenle" onClick={() => startEdit(t.id, t.name)} disabled={editingId !== null}>
                      <Icon name="edit" size={15} />
                    </button>
                    <ToggleBtn active={t.isActive} busy={toggleMut.isPending} onClick={() => toggleMut.mutate({ id: t.id, isActive: !t.isActive })} />
                  </div>
                </div>
              )
            )}
          </div>
        )}
      </div>
    </section>
  );
}

/* ─── Özel Alanlar ───────────────────────────────────────── */
function CustomFieldsSection() {
  const qc = useQueryClient();
  const isMobile = useIsMobile();
  const listQ = trpc.settings.customField.list.useQuery({ includeInactive: true });
  const toggleMut = trpc.settings.customField.toggle.useMutation({ onSuccess: () => qc.invalidateQueries() });
  const [addOpen, setAddOpen] = useState(false);
  const rows = listQ.data ?? [];

  if (isMobile) {
    return (
      <>
        <div className="m-stickytop">
          <button type="button" className="btn-md primary" style={{ justifyContent: "center" }} onClick={() => setAddOpen(true)}><Icon name="plus" size={14} />Yeni Alan</button>
        </div>
        <p className="sub" style={{ margin: 0, fontSize: 12 }}>Buraya eklediğin alanlar, ilgili kart tipinin formunda otomatik görünür.</p>
        {rows.length === 0 ? <div className="m-empty">Henüz özel alan tanımlanmadı.</div> : (
          rows.map((f) => (
            <MCard pad key={f.id}>
              <div style={{ display: "flex", alignItems: "flex-start", gap: 8 }}>
                <span style={{ flex: 1, minWidth: 0 }}>
                  <span style={{ display: "block", fontSize: 13.5, fontWeight: 700, color: "var(--neutral-900)", opacity: f.isActive ? 1 : 0.5 }}>{f.label}{f.required ? <span style={{ color: "var(--danger)", marginLeft: 3 }}>*</span> : null}</span>
                  <span className="mono" style={{ display: "block", fontSize: 11, color: "var(--neutral-400)" }}>{f.code}</span>
                </span>
                {!f.isActive ? <span className="badge b-neutral no-dot">Pasif</span> : null}
                <button type="button" className={`m-iconbtn m-li-act${f.isActive ? " danger" : ""}`} style={!f.isActive ? { color: "var(--success)" } : undefined} aria-label={f.isActive ? "Pasifleştir" : "Aktifleştir"} onClick={() => toggleMut.mutate({ id: f.id, isActive: !f.isActive })} disabled={toggleMut.isPending}><Icon name={f.isActive ? "archive" : "check"} size={16} /></button>
              </div>
              <div style={{ display: "flex", gap: 16, marginTop: 8, flexWrap: "wrap" }}>
                <div><div className="m-ovl">Tip</div><div style={{ fontSize: 12, fontWeight: 600, marginTop: 2 }}>{TYPE_LABEL[f.dataType] ?? f.dataType}{f.unit ? ` · ${f.unit}` : ""}</div></div>
                <div><div className="m-ovl">Kart</div><div style={{ fontSize: 12, fontWeight: 600, marginTop: 2 }}>{f.appliesTo ? KIND_LABEL[f.appliesTo as ItemKind] : "Tüm kartlar"}</div></div>
              </div>
              {f.dataType === "select" && f.options.length ? <div style={{ fontSize: 11.5, color: "var(--neutral-600)", marginTop: 6 }}>[{f.options.join(", ")}]</div> : null}
            </MCard>
          ))
        )}
        <CustomFieldDrawer open={addOpen} onClose={() => setAddOpen(false)} />
      </>
    );
  }

  return (
    <section className="d-card">
      <div className="d-card-head">
        <div className="left"><span className="t">Özel Alanlar</span><span className="s">{rows.length} alan</span></div>
        <button type="button" className="btn-md primary" style={{ height: 28, padding: "0 10px", fontSize: 12 }} onClick={() => setAddOpen(true)}><Icon name="plus" size={13} />Yeni Alan</button>
      </div>
      <div className="d-card-body">
        <p className="sub" style={{ marginBottom: 14, fontSize: 12 }}>Buraya eklediğin alanlar, ilgili kart tipinin formunda otomatik görünür.</p>
        {rows.length === 0 ? (
          <div className="d-empty"><Icon name="settings" size={24} /><span className="m">Henüz özel alan tanımlanmadı.</span></div>
        ) : (
          <div className="list-card" style={{ border: "1px solid var(--neutral-200)", borderRadius: "var(--radius-md)" }}>
            {rows.map((f) => (
              <div key={f.id} className="list-item">
                <span className="body" style={{ opacity: f.isActive ? 1 : 0.5 }}>
                  <span className="t">{f.label}{f.required ? <span style={{ color: "var(--danger)", marginLeft: 3 }}>*</span> : null}</span>
                  <span className="s">
                    <span className="mono">{f.code}</span> · {TYPE_LABEL[f.dataType] ?? f.dataType}
                    {f.unit ? ` · ${f.unit}` : ""} · {f.appliesTo ? KIND_LABEL[f.appliesTo as ItemKind] : "Tüm kartlar"}
                    {f.dataType === "select" && f.options.length ? ` · [${f.options.join(", ")}]` : ""}
                  </span>
                </span>
                {!f.isActive ? <span className="mono" style={{ fontSize: 10.5, color: "var(--neutral-400)" }}>pasif</span> : null}
                <div className="row-acts"><ToggleBtn active={f.isActive} busy={toggleMut.isPending} onClick={() => toggleMut.mutate({ id: f.id, isActive: !f.isActive })} /></div>
              </div>
            ))}
          </div>
        )}
      </div>
      <CustomFieldDrawer open={addOpen} onClose={() => setAddOpen(false)} />
    </section>
  );
}

function CustomFieldDrawer({ open, onClose }: { open: boolean; onClose: () => void }) {
  const qc = useQueryClient();
  const createMut = trpc.settings.customField.create.useMutation({ onSuccess: () => { qc.invalidateQueries(); onClose(); } });
  const [code, setCode] = useState("");
  const [label, setLabel] = useState("");
  const [dataType, setDataType] = useState<"number" | "text" | "select" | "boolean">("number");
  const [unit, setUnit] = useState("");
  const [optionsRaw, setOptionsRaw] = useState("");
  const [appliesTo, setAppliesTo] = useState<"" | ItemKind>("");
  const [required, setRequired] = useState(false);

  // open olunca formu sıfırla
  useEffect(() => { if (open) { setCode(""); setLabel(""); setDataType("number"); setUnit(""); setOptionsRaw(""); setAppliesTo(""); setRequired(false); createMut.reset(); } }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

  function submit() {
    const options = optionsRaw.split(",").map((s) => s.trim()).filter(Boolean);
    createMut.mutate({
      code: code.trim(),
      label: label.trim(),
      dataType,
      unit: unit.trim() || undefined,
      options,
      appliesTo: appliesTo || null,
      required,
    });
  }

  return (
    <Drawer
      open={open}
      onClose={onClose}
      title="Yeni Özel Alan"
      subtitle="Kart formuna eklenecek alan tanımı"
      footer={
        <>
          <button type="button" className="btn-md ghost" onClick={onClose} disabled={createMut.isPending}>İptal</button>
          <button type="button" className="btn-md primary" onClick={submit} disabled={!code.trim() || !label.trim() || createMut.isPending}>{createMut.isPending ? "Kaydediliyor…" : "Ekle"}</button>
        </>
      }
    >
      {createMut.error ? <div className="form-banner-err">{createMut.error.message}</div> : null}
      <div className="form-row" style={{ gridTemplateColumns: "1fr 1fr" }}>
        <div className="form-field">
          <label className="lbl">Kod <span className="req">*</span></label>
          <input className="form-input" value={code} onChange={(e) => setCode(e.target.value)} placeholder="ikinci_kalite" maxLength={50} />
          <span className="help">Benzersiz; harf/rakam/_/-</span>
        </div>
        <div className="form-field">
          <label className="lbl">Etiket <span className="req">*</span></label>
          <input className="form-input" value={label} onChange={(e) => setLabel(e.target.value)} placeholder="İkinci Kalite" maxLength={100} />
        </div>
      </div>
      <div className="form-row" style={{ gridTemplateColumns: "1fr 1fr" }}>
        <div className="form-field">
          <label className="lbl">Tip</label>
          <select className="form-select" value={dataType} onChange={(e) => setDataType(e.target.value as typeof dataType)}>
            <option value="number">Sayı</option>
            <option value="text">Metin</option>
            <option value="select">Seçim (liste)</option>
            <option value="boolean">Evet / Hayır</option>
          </select>
        </div>
        <div className="form-field">
          <label className="lbl">Uygulanacak Kart</label>
          <select className="form-select" value={appliesTo} onChange={(e) => setAppliesTo(e.target.value as "" | ItemKind)}>
            <option value="">Tüm kartlar</option>
            <option value="stock">Stok</option>
            <option value="semi">Yarı Mamul</option>
            <option value="finished">Mamul</option>
          </select>
        </div>
      </div>
      {dataType === "number" || dataType === "select" ? (
        <div className="form-row" style={{ gridTemplateColumns: "1fr 1fr" }}>
          {dataType === "number" ? (
            <div className="form-field" style={{ marginBottom: 0 }}>
              <label className="lbl">Birim (opsiyonel)</label>
              <input className="form-input" value={unit} onChange={(e) => setUnit(e.target.value)} placeholder="kg, adet, %" maxLength={20} />
            </div>
          ) : (
            <div className="form-field" style={{ marginBottom: 0 }}>
              <label className="lbl">Seçenekler <span className="req">*</span></label>
              <input className="form-input" value={optionsRaw} onChange={(e) => setOptionsRaw(e.target.value)} placeholder="A Sınıfı, B Sınıfı, C Sınıfı" />
              <span className="help">Virgülle ayırın.</span>
            </div>
          )}
          <div className="form-field" style={{ marginBottom: 0, justifyContent: "flex-end" }}>
            <label className="form-checkbox" style={{ height: 36 }}>
              <input type="checkbox" checked={required} onChange={(e) => setRequired(e.target.checked)} />
              <span>Zorunlu alan</span>
            </label>
          </div>
        </div>
      ) : (
        <div className="form-field">
          <label className="form-checkbox">
            <input type="checkbox" checked={required} onChange={(e) => setRequired(e.target.checked)} />
            <span>Zorunlu alan</span>
          </label>
        </div>
      )}
    </Drawer>
  );
}

// ── Sistem Bakımı: Transactional Veri Temizliği (süper admin) ─────────────
// Üretim/sevkiyat/sipariş + üretilen lotları siler; ham madde (mal kabul) lotları
// ve MEVCUT bakiyeleri KORUNUR. Aynı motoru (systemMaintenance router) canlı API
// içinde çift onayla çalıştırır — SSH/komut gerektirmez.
function MaintenanceSection() {
  const qc = useQueryClient();
  const isMobile = useIsMobile();
  const prevQ = trpc.systemMaintenance.transactionalPreview.useQuery();
  const resetMut = trpc.systemMaintenance.resetTransactional.useMutation();
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [doneMsg, setDoneMsg] = useState<string | null>(null);

  // Salt-okunur iş emri ağacı teşhisi (destek/hata analizi için — hiçbir şey değiştirmez).
  const [diagCodes, setDiagCodes] = useState("WO-2026-00030, WO-2026-00046, WO-2026-00064");
  const [diagCopied, setDiagCopied] = useState(false);
  const diagArr = diagCodes.split(",").map((s) => s.trim()).filter(Boolean);
  const diagQ = trpc.systemMaintenance.woTreeDiagnostic.useQuery({ woCodes: diagArr }, { enabled: false, retry: false });
  const diagText = diagQ.data ? JSON.stringify(diagQ.data, null, 2) : "";

  const p = prevQ.data;
  function fmt(n: number): string {
    return new Intl.NumberFormat("tr-TR", { maximumFractionDigits: 3 }).format(n);
  }

  function runReset() {
    resetMut.mutate({ confirmPhrase: "TEMİZLE" }, {
      onSuccess: (res) => {
        setConfirmOpen(false);
        const d = res.deleted;
        setDoneMsg(`Temizlendi — ${d.orders} sipariş, ${d.shipments} sevkiyat, ${d.workOrders} iş emri, ${d.batches} parti, ${d.producedLots} üretilen lot silindi. Korunan ham madde: ${res.keptAfter.rawLots} lot, Σ ${fmt(res.keptAfter.rawStockBalance)} bakiye (değişmedi).`);
        qc.invalidateQueries();
      },
    });
  }

  return (
    <div style={{ maxWidth: isMobile ? "100%" : 720 }}>
      {/* Salt-okunur teşhis — üretim/iş emri hata analizi için (hiçbir şey değiştirmez) */}
      <div className="d-card" style={{ marginBottom: 16 }}>
        <div className="d-card-head">
          <div className="left"><span className="t"><Icon name="list2" size={14} /> İş Emri Ağacı Teşhisi</span></div>
        </div>
        <div className="d-card-body">
          <p style={{ fontSize: 13, color: "var(--neutral-700)", lineHeight: 1.6, margin: "0 0 10px" }}>
            <b>Salt-okunur</b> — verilen iş emri kodlarının siparişini, tüm iş emri ağacını (planlanan/üretilen/kalan + üst-WO + kaynak sipariş) ve paylaşım özetini çıkarır. Destek/hata analizi içindir, <b>hiçbir şey değiştirmez.</b>
          </p>
          <label className="form-label" style={{ fontSize: 12 }}>İş Emri Kodları (virgülle)</label>
          <input className="form-input" value={diagCodes} onChange={(e) => { setDiagCodes(e.target.value); setDiagCopied(false); }} style={{ marginBottom: 10, fontFamily: "var(--font-mono)", fontSize: 12.5 }} placeholder="WO-2026-00030, WO-2026-00046" />
          <div style={{ display: "flex", gap: 8, marginBottom: 10 }}>
            <button type="button" className="btn-md btn-primary" disabled={diagQ.isFetching || diagArr.length === 0} onClick={() => { setDiagCopied(false); diagQ.refetch(); }}>
              <Icon name="list2" size={14} />{diagQ.isFetching ? "Çıkarılıyor…" : "Teşhis Çıkar"}
            </button>
            {diagText ? (
              <button type="button" className="btn-md" onClick={() => { navigator.clipboard?.writeText(diagText); setDiagCopied(true); }}>
                <Icon name="copy" size={14} />{diagCopied ? "Kopyalandı ✓" : "Kopyala"}
              </button>
            ) : null}
          </div>
          {diagQ.isError ? <div className="form-banner-err" style={{ marginBottom: 10 }}>{diagQ.error?.message}</div> : null}
          {diagText ? (
            <pre style={{ background: "var(--neutral-900)", color: "var(--neutral-50)", borderRadius: "var(--radius-md)", padding: "12px 14px", fontSize: 11.5, lineHeight: 1.5, overflowX: "auto", maxHeight: 460, margin: 0 }}>{diagText}</pre>
          ) : null}
        </div>
      </div>

      <div className="d-card" style={{ border: "1px solid var(--danger)" }}>
        <div className="d-card-head">
          <div className="left"><span className="t" style={{ color: "var(--danger)" }}><Icon name="alertTriangle" size={14} /> Transactional Veri Temizliği</span></div>
        </div>
        <div className="d-card-body">
          {doneMsg ? <div className="form-banner-ok" role="status" style={{ marginBottom: 12 }}>{doneMsg}</div> : null}

          <p style={{ fontSize: 13, color: "var(--neutral-700)", lineHeight: 1.6, margin: "0 0 10px" }}>
            Üretim, sevkiyat ve müşteri siparişi kayıtlarını + <b>üretilen (yarı mamul/mamul) lotları</b> kalıcı olarak siler.
            Sistemi canlı veriyle sıfırdan başlatmak içindir.
          </p>
          <div className="form-banner-info" style={{ marginBottom: 12 }}>
            <div>
              <b>Ham madde (mal kabul) lotları ve mevcut bakiyeleri KORUNUR</b> — tüketilen ham madde geri yüklenmez, bakiyeler olduğu gibi kalır.
              Satın alma siparişleri, sertifikalar ve tüm master veri (ürün/reçete/cari/lokasyon/kullanıcı) korunur.
            </div>
          </div>
          <div className="form-banner-err" style={{ marginBottom: 14 }}>
            <div><b>Geri alınamaz.</b> Devam etmeden önce veritabanı yedeğinizi aldığınızdan emin olun.</div>
          </div>

          {prevQ.isLoading ? (
            <div style={{ color: "var(--neutral-500)", fontSize: 13 }}>Önizleme yükleniyor…</div>
          ) : prevQ.isError ? (
            <div className="form-banner-err">{prevQ.error?.message}</div>
          ) : p ? (
            <div style={{ display: "grid", gridTemplateColumns: isMobile ? "1fr" : "1fr 1fr", gap: 12, marginBottom: 14 }}>
              <div style={{ background: "var(--danger-subtle)", borderRadius: "var(--radius-md)", padding: "10px 12px" }}>
                <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: "0.04em", textTransform: "uppercase", color: "var(--danger)", marginBottom: 6 }}>Silinecek</div>
                <ul style={{ margin: 0, paddingLeft: 16, fontSize: 12.5, lineHeight: 1.7, color: "var(--neutral-700)" }}>
                  <li>Sipariş: <b className="mono">{p.willDelete.orders}</b> (satır {p.willDelete.orderLines}, rezervasyon {p.willDelete.reservations})</li>
                  <li>Sevkiyat: <b className="mono">{p.willDelete.shipments}</b> (satır {p.willDelete.shipmentLines})</li>
                  <li>İş emri: <b className="mono">{p.willDelete.workOrders}</b></li>
                  <li>Üretim partisi: <b className="mono">{p.willDelete.batches}</b></li>
                  <li>Üretilen lot: <b className="mono">{p.willDelete.producedLots}</b> (hareket {p.willDelete.movementsOnProduced})</li>
                </ul>
              </div>
              <div style={{ background: "var(--success-subtle)", borderRadius: "var(--radius-md)", padding: "10px 12px" }}>
                <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: "0.04em", textTransform: "uppercase", color: "var(--success)", marginBottom: 6 }}>Korunacak</div>
                <ul style={{ margin: 0, paddingLeft: 16, fontSize: 12.5, lineHeight: 1.7, color: "var(--neutral-700)" }}>
                  <li>Ham madde lot: <b className="mono">{p.willKeep.rawLots}</b> / {p.willKeep.totalLots}</li>
                  <li>Ham madde bakiyesi Σ: <b className="mono">{fmt(p.willKeep.rawStockBalance)}</b></li>
                  <li>Satın alma (PO): <b className="mono">{p.willKeep.purchaseOrders}</b></li>
                  <li>Sertifika: <b className="mono">{p.willKeep.certificates}</b></li>
                </ul>
              </div>
            </div>
          ) : null}

          <button type="button" className="btn-md" style={{ background: "var(--danger)", color: "var(--white)", borderColor: "var(--danger)" }}
            disabled={resetMut.isPending || prevQ.isLoading}
            onClick={() => { setDoneMsg(null); setConfirmOpen(true); }}>
            <Icon name="trash" size={14} />{resetMut.isPending ? "Temizleniyor…" : "Transactional Veriyi Temizle"}
          </button>
          {resetMut.error && !confirmOpen ? <div className="form-banner-err" style={{ marginTop: 10 }}>{resetMut.error.message}</div> : null}
        </div>
      </div>

      <HardDeleteConfirm
        open={confirmOpen}
        code="TEMİZLE"
        title="Transactional veriyi temizle"
        warning={<>Tüm üretim, sevkiyat, sipariş kayıtları ve üretilen lotlar <b>kalıcı olarak</b> silinecek. Ham madde stoğu korunur. Bu işlem <b>geri alınamaz</b> — yedeğinizi aldınız mı?</>}
        busy={resetMut.isPending}
        errorMessage={resetMut.error?.message ?? null}
        onConfirmed={runReset}
        onClose={() => setConfirmOpen(false)}
      />
    </div>
  );
}

/* ─── Bordro Politikası (Faz 3) — YALNIZ SÜPER-ADMIN ────────────────────────────
   Bu sayfadaki diğer bölümler `update:item` ile korunur; bu uç `assertSalaryAccess`
   (salt süper-admin) kullanır — maaş kesinti kuralları stok düzenleyicisine açılamaz.
   Sekme de `isSuperAdmin` ile iki kez kapılıdır (sekme çubuğu + zincir içi). */
function PayrollPolicySection() {
  const qc = useQueryClient();
  const isMobile = useIsMobile();
  const getQ = trpc.settings.payrollPolicy.get.useQuery(undefined, { staleTime: 30_000 });
  const meQ = trpc.auth.me.useQuery(undefined, { staleTime: 5 * 60_000 });
  const tesisName = meQ.data?.user?.facilities?.find((f: { id: string; name: string }) => f.id === getQ.data?.tesisId)?.name ?? null;
  const [err, setErr] = useState<string | null>(null);
  const [ok, setOk] = useState(false);

  const [dayBasis, setDayBasis] = useState<"fixed30" | "workdays">("fixed30");
  const [divisor, setDivisor] = useState("225");
  const [multiplier, setMultiplier] = useState("1,5");
  const [deductAbsence, setDeductAbsence] = useState(true);
  const [deductUnpaidLeave, setDeductUnpaidLeave] = useState(true);
  const [payOvertime, setPayOvertime] = useState(true);

  /**
   * Sunucudan gelen değerleri forma bas — ama TESİS DEĞİŞİNCE yeniden bas.
   * Tek seferlik doldurma hatalıydı: süper-admin üst çubuktan tesis değiştirince sorgu
   * yeni tesisin politikasını çekiyor, form ESKİ tesisin değerlerini gösteriyor ve
   * "Kaydet" onları YENİ tesise yazıyordu.
   */
  const loadedRef = useRef<string | null>(null);
  useEffect(() => {
    if (!getQ.data) return;
    const key = getQ.data.tesisId ?? "__global__";
    if (loadedRef.current === key) return;
    loadedRef.current = key;
    setDayBasis(getQ.data.dayBasis as "fixed30" | "workdays");
    setDivisor(String(getQ.data.overtimeDivisor).replace(".", ","));
    setMultiplier(String(getQ.data.overtimeMultiplier).replace(".", ","));
    setDeductAbsence(getQ.data.deductAbsence);
    setDeductUnpaidLeave(getQ.data.deductUnpaidLeave);
    setPayOvertime(getQ.data.payOvertime);
  }, [getQ.data]);

  const saveMut = trpc.settings.payrollPolicy.update.useMutation({
    onSuccess: () => { qc.invalidateQueries(); setErr(null); setOk(true); setTimeout(() => setOk(false), 3000); },
    onError: (e) => { setOk(false); setErr(e.message); },
  });

  const divNum = parseTrNumber(divisor);
  const mulNum = parseTrNumber(multiplier);
  const valid = divNum !== null && divNum >= 1 && mulNum !== null && mulNum >= 0;

  const save = () => {
    if (!valid) { setErr("Bölen en az 1, katsayı en az 0 olmalı."); return; }
    saveMut.mutate({
      dayBasis, overtimeDivisor: divNum!, overtimeMultiplier: mulNum!,
      deductAbsence, deductUnpaidLeave, payOvertime,
    });
  };

  const content = (
    <>
      <div className="set-sec">
        <span className="set-sec-title">Bordro Politikası</span>
        <p className="set-hint">
          Bordro "Hesapla" dediğinizde uygulanan kurallar. Değişiklik <b>geçmiş bordroları etkilemez</b> —
          her dönem kendi oranlarını hesap anında dondurur.
        </p>
        {/* Hangi tesis düzenleniyor — politika tesis başınadır, belirsiz kalmamalı. */}
        {tesisName ? <p className="set-hint"><b>Düzenlenen tesis:</b> {tesisName}</p> : null}
      </div>

      {getQ.data && !getQ.data.hasPolicy ? (
        <div className="form-banner-info">
          <Icon name="info" size={14} style={{ flexShrink: 0, marginTop: 2 }} />
          <div>Bu tesiste bordro politikası kayıtlı değil — varsayılan <b>30 gün / 225 saat / 1,5 katsayı</b> uygulanıyor. Kaydedince kalıcı olur.</div>
        </div>
      ) : null}
      {err ? <div className="form-banner-err" role="alert">{err}</div> : null}
      {ok ? <div className="form-banner-ok" role="status">Bordro politikası kaydedildi.</div> : null}

      <div className="form-field">
        <label className="lbl" htmlFor="pp-basis">Günlük ücret böleni</label>
        <select id="pp-basis" className="form-select" value={dayBasis} onChange={(e) => setDayBasis(e.target.value as "fixed30" | "workdays")}>
          <option value="fixed30">Sabit 30 gün (brüt ÷ 30)</option>
          <option value="workdays">Ayın iş günü (brüt ÷ o ayın iş günü)</option>
        </select>
        <span className="help">
          Devamsızlık ve ücretsiz izin kesintisi bu günlük ücret üzerinden hesaplanır.
          Sabit 30, aylar arasında değişmeyen tutar verir.
        </span>
      </div>

      <div className="form-row">
        <div className="form-field">
          <label className="lbl" htmlFor="pp-div">Saatlik ücret böleni</label>
          <input id="pp-div" className="form-input mono" value={divisor} onChange={(e) => setDivisor(e.target.value)} inputMode="decimal" />
          <span className="help">Saatlik ücret = brüt ÷ bu sayı. Yaygın uygulama 225 (30 gün × 7,5 saat).</span>
        </div>
        <div className="form-field">
          <label className="lbl" htmlFor="pp-mul">Fazla mesai katsayısı</label>
          <input id="pp-mul" className="form-input mono" value={multiplier} onChange={(e) => setMultiplier(e.target.value)} inputMode="decimal" />
          <span className="help">Mesai ödemesi = saatlik ücret × katsayı × mesai saati.</span>
        </div>
      </div>

      <div className="form-field">
        <span className="lbl">Uygulanacak kalemler</span>
        <label className="form-checkbox">
          <input type="checkbox" checked={deductAbsence} onChange={(e) => setDeductAbsence(e.target.checked)} />
          <span>Devamsızlık günlerini ücretten kes</span>
        </label>
        <label className="form-checkbox">
          <input type="checkbox" checked={deductUnpaidLeave} onChange={(e) => setDeductUnpaidLeave(e.target.checked)} />
          <span>Ücretsiz izin günlerini ücretten kes</span>
        </label>
        <label className="form-checkbox">
          <input type="checkbox" checked={payOvertime} onChange={(e) => setPayOvertime(e.target.checked)} />
          <span>Fazla mesaiyi ücrete ekle</span>
        </label>
        <span className="help">
          Üçünü de kapatırsanız bordro, bu özellikten önceki gibi yalnız
          <b> brüt + ek ödeme − kesinti</b> hesaplar.
        </span>
      </div>

      <div className="set-sec">
        <p className="set-hint">
          <b>Önemli:</b> devamsızlık YALNIZ puantajda "Devamsız" veya "Yarım Gün" yazan günlerden sayılır.
          Puantajı hiç girilmemiş bir ayda kesinti yapılmaz — kimse eksik maaş almaz.
        </p>
      </div>

      <div style={{ marginTop: 8 }}>
        <button type="button" className="btn-md primary" disabled={!valid || saveMut.isPending} onClick={save}>
          {saveMut.isPending ? "Kaydediliyor…" : "Kaydet"}
        </button>
      </div>
    </>
  );

  if (getQ.isLoading) return <p className="td-empty">Yükleniyor…</p>;
  if (isMobile) return <div className="set-mform">{content}</div>;
  return <div style={{ maxWidth: 720 }}>{content}</div>;
}

